import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useBrandName } from '@/lib/branding';
import { buildHelpGroups } from '@/config/helpNav';
import { HELP_SECTIONS_EXTRA } from '@/data/helpSectionsExtra';
import {
  BookOpen, Search, ChevronRight, BarChart3, Calculator, TrendingUp,
  Layers, GitBranch, Table, Sparkles, Clock, Filter, ArrowLeftRight, Wand2, FileText, Brain, Building2,
  Home, Zap, List, Database, Workflow, BookMarked, Bell, CalendarClock, AlertTriangle, MessageSquare,
  ShieldCheck, Activity, Settings as SettingsIcon, Gauge, Network, Code as CodeIcon, Trash2, Mail, Inbox, Ticket, UserCheck, Server, Briefcase, ThumbsUp, ThumbsDown, Plus, Pencil, Save, Lightbulb,
} from 'lucide-react';

// String → icon component, so DB-stored articles can carry an icon name.
const ICON_MAP = {
  BookOpen, BarChart3, Calculator, TrendingUp, Layers, GitBranch, Table, Sparkles, Clock, Filter,
  ArrowLeftRight, Wand2, FileText, Brain, Building2, Home, Zap, List, Database, Workflow, BookMarked,
  Bell, CalendarClock, AlertTriangle, MessageSquare, ShieldCheck, Activity, SettingsIcon, Gauge,
  Network, CodeIcon, Mail, Inbox, Ticket, UserCheck, Server, Briefcase, Lightbulb,
};
const iconFor = (name) => ICON_MAP[name] || BookOpen;
// Reverse map (component identity → name) for seeding from the in-code SECTIONS.
const _COMPONENT_TO_NAME = new Map(Object.entries(ICON_MAP).map(([n, c]) => [c, n]));
const iconName = (comp) => _COMPONENT_TO_NAME.get(comp) || 'BookOpen';

const SECTIONS = [
  {
    id: 'llm-providers', title: 'AI / LLM Providers (Bring Your Own)', icon: Sparkles,
    content: `
## AI / LLM Providers

INZARA is **model-agnostic**. Every AI feature can be powered by the provider and model *you* choose. Self-hosted customers bring their own API keys; the hosted edition ships with a built-in universal key.

Open **Admin → AI / LLM Providers** (\`/admin/llm\`). Requires a tenant or super admin.

![The AI / LLM Providers admin — configure any provider and route each AI feature to a chosen model](/help-assets/llm-providers.png)

### Supported providers

| Provider | Credentials needed | Notes |
|----------|--------------------|-------|
| **Emergent Universal Key** | none | Built-in default (serves OpenAI / Anthropic / Gemini). Zero setup. |
| **OpenAI** | API key | gpt-5.4, gpt-4o, gpt-4.1, o4-mini, … |
| **Anthropic** | API key | Claude Sonnet / Haiku / Opus |
| **Google Gemini** | API key | Gemini 3.x Pro / Flash |
| **Azure OpenAI** | API key, endpoint, API version, deployment | Data stays in your Azure tenancy |
| **AWS Bedrock** | IAM (IRSA) **or** access key + region | Claude / Nova on Bedrock; keeps data in your AWS account |

### Configure a provider
1. Toggle the provider **on** and fill its credentials.
2. Click **Save** — secrets are stored encrypted and only ever shown masked (\`••••abcd\`).
3. Click **Test connection** to confirm it responds.

### Per-feature model routing
The **Per-feature model routing** table lets you send each AI task to a different model — e.g. a fast cheap model for field classification, a stronger model for natural-language queries.

| Feature | What it powers |
|---------|----------------|
| Natural-language → query | Explore / "Ask" |
| Auto insights & summaries | Narrative insights |
| Data Prep AI copilot | Plain-English pipeline steps |
| AI dashboard / report builder | Generated dashboards |
| Semantic modeling assistant | Relationship / field suggestions |
| Report import parsing | Tableau / PBIX / LookML import |
| AI field classification | Dimension vs measure detection |
| Anomaly explanation, Root-cause analysis | Analytics narratives |
| General / fallback | Everything else |

Leave a feature on **Use default** to inherit the **Default (fallback)** row. If you leave the Emergent key blank in a self-hosted install, you **must** configure at least one provider.

> **Tip:** AWS Bedrock can authenticate via the pod's IAM role (IRSA) — no keys to type in. See *Self-Hosted Deployment*.
`
  },
  {
    id: 'scoped-measures', title: 'Scoped, Context & % Measures', icon: Calculator,
    content: `
## Scoped, Context-switching & % of Total Measures

Beyond simple formulas, INZARA measures support **set algebra** and **filter-context switching** — the same power as Tableau LOD expressions and Power BI's CALCULATE / ALL.

Create these from **Semantic Model → Measure → "Scoped (set filter)"** mode.

![The Add Calculated Measure dialog — scope filters, context-switch (ignore filters) and one-click % of grand total](/help-assets/scoped-measure-dialog.png)

### Scoped measures (set algebra)
A scoped measure aggregates a base field **only over rows matching its own filters**, independent of the chart's dimensions.

- Example: *Revenue (West)* = \`SUM(written_premium_m)\` where \`region = West\`.
- Resolved entirely on the server during aggregation — fast and exact.

### Context-switching (Ignore filters)
The **"Ignore filters on"** box lets a measure break out of the dashboard/query filter context (DAX \`ALL()\` / \`REMOVEFILTERS()\`).

- Enter dimension names (e.g. \`year, region\`) or \`*\` to ignore **every** filter.
- Example: with a \`year = 2023\` slicer active, a measure that ignores \`year\` returns the **all-years** total alongside the filtered value.

### % of grand total (one click)
Tick **"Show as % of grand total"** to turn any measure into its share of the column total — perfect for mix analysis. Values are computed post-aggregation and always sum to 100%.

### Point-in-time (snapshot-date) measures
A snapshot metric — such as *Inforce Policy Count* on a monthly summary fact — is only meaningful **at one date**; summing it across months would double-count. In the Scoped editor turn on **"Requires a snapshot date"** and pick the date field (e.g. \`BOOK_END_DATE\`).

- The measure is then **pinned to a single snapshot date** on every query surface (Explore, builders, KPIs, schedules) — it never sums across periods.
- **When a date is chosen**, pick how it resolves: *Exact date match*, or *As-of (latest on or before)*.
- **When no date is chosen**, the result auto-defaults to the **latest available snapshot** and is labelled with the date used.
- Grouping the visual **by** the snapshot date shows the true value **per period** (each period is its own snapshot) rather than a single pinned date.

| Measure type | Tableau / Power BI equivalent |
|--------------|-------------------------------|
| Scoped (set filter) | LOD \`{ FIXED }\` / \`CALCULATE(..., filter)\` |
| Ignore filters | \`CALCULATE(..., ALL(dim))\` / \`REMOVEFILTERS()\` |
| % of grand total | "Show value as % of grand total" |
| Requires snapshot date | Semi-additive "point-in-time" measure |

> These work on both the MongoDB/DocumentDB engine and the SQL push-down engine (Postgres, Snowflake, Redshift, BigQuery).
`
  },
  {
    id: 'promotion', title: 'Dev → Test → Prod & Approvals', icon: GitBranch,
    content: `
## Promotion Pipeline & Approval Gate

Reports, dashboards and apps move through **Dev → Test → Production** stages so you ship changes safely.

![The promotion dialog — move reports & dashboards between Dev, Test and Production stages](/help-assets/promotion.png)

### Promote
1. Open a report/app and choose **Promote**.
2. Review the **diff** (what changed vs the target stage).
3. Confirm to copy the content to the next stage.

### Approval gate
For governed environments, promotion to Production can require sign-off:
1. The author clicks **Request approval** → a **Pending** approval is created.
2. An approver opens the approvals queue, reviews the diff, and **Approves** or **Rejects**.
3. On approval the promotion fires automatically; on rejection nothing changes.

Viewers always see the **Production** stage; editors work in Dev/Test without affecting live dashboards.
`
  },
  {
    id: 'data-prep-transforms', title: 'Data Prep — Transform Library (46)', icon: Workflow,
    content: `
## Data Prep — Transform Library

The visual ETL editor (**Data → Data Prep**) ships **46 transforms**. Drag steps onto the pipeline, or use the **AI copilot** ("bucket age into bands", "dedupe by email") to generate steps from plain English.

![The visual Data Prep pipeline editor with its drag-and-drop transform library](/help-assets/data-prep.png)

### Column & text
| Transform | What it does |
|-----------|--------------|
| Rename / Cast / Select / Drop Columns | Reshape columns |
| Clean Text / Text Transform | Trim, upper/lower, title-case |
| Split Column / Concat Columns | Split or join text |
| Substring / Text Length | Extract part / character count |
| Regex Extract / Regex Replace | Pattern match & replace |
| Map Values | Lookup-table remap |
| Conditional Column | If/then derived column |

### Numeric & statistical
| Transform | What it does |
|-----------|--------------|
| Math Function | abs / log / sqrt / exp / ceil / floor |
| Round / Clip (clamp) | Round or bound values |
| Normalize (0–1) / Z-Score | Scale or standardize |
| N-Tile / Quantile / Rank | Bucket or rank rows |
| Running Total / % of Total | Cumulative & share |

### Window & time
| Transform | What it does |
|-----------|--------------|
| Lag / Lead | Previous / next row value |
| Moving Average | Rolling window mean |
| Add Row Index / Fill Down | Sequence / forward-fill nulls |
| Year-Month / Extract Date Part | Date grains |
| Date Difference / Date Add | Date math |

### Rows & sets
| Transform | What it does |
|-----------|--------------|
| Filter / Sort / Dedupe | Row operations |
| Group By | Roll up with aggregates |
| Union / Join | Combine datasets |
| Coalesce | First non-null |

> Each step previews its output instantly so you can validate as you build.
`
  },
  {
    id: 'self-hosting', title: 'Self-Hosted Deployment (AWS/K8s)', icon: Server,
    content: `
## Self-Hosted Deployment

INZARA can run entirely inside your own AWS account on **Amazon EKS**, packaged as a Helm chart (\`deploy/helm/inzara\`). Full instructions live in \`deploy/SELF_HOSTING.md\`.

### Architecture
- **Frontend** — React build served by Nginx (runtime-injected backend URL).
- **Backend** — FastAPI (stateless, autoscaled via HPA).
- **Database** — Amazon DocumentDB 5.0+ (recommended) or self-managed MongoDB.
- **Object storage** — your S3 bucket (uploads, exports, thumbnails).
- **Ingress** — AWS ALB + ACM TLS.

### Security-first defaults
| Concern | Approach |
|---------|----------|
| AWS access | **IRSA** (pod IAM role) — no static keys for S3 / Bedrock |
| Secrets | **AWS Secrets Manager** via External Secrets Operator |
| Containers | Non-root, health-checked, resource-limited |
| LLM data | Configure Azure OpenAI / Bedrock to keep prompts in your tenancy |

### DocumentDB compatibility
Set \`DB_COMPAT=documentdb\` to enable DocumentDB-safe query paths (the engine swaps out the \`$facet\` and \`$round\` operators DocumentDB lacks for equivalent fallbacks). Use engine **5.0+**.

### Bring-your-own AI
After install, configure providers under **Admin → AI / LLM Providers**. Leave the Emergent key blank to require your own OpenAI / Anthropic / Gemini / Azure / Bedrock credentials.
`
  },

  {
    id: 'formulas', title: 'Formulas & Calculated Measures', icon: Calculator,
    content: `
## Formulas & Calculated Measures

Calculated measures let you define custom metrics using formulas that reference existing fields. They are computed after aggregation, so you can safely divide aggregated values.

### Creating a Calculated Measure
1. Open a **Semantic Model** → click **Measure** in the toolbar
2. Enter a name, formula, and optional description
3. The formula can reference any attribute or measure by its field name

### Formula Syntax

| Operator | Example | Description |
|----------|---------|-------------|
| + - * / | \`written_premium_m - earned_premium_m\` | Basic arithmetic |
| ( ) | \`(paid_losses + reserves) / earned_premium\` | Grouping |
| Field names | \`incurred_losses_m / earned_premium_m\` | Reference model fields |

### Example Formulas

| Measure | Formula | Description |
|---------|---------|-------------|
| Net Premium | \`written_premium_m - earned_premium_m\` | Difference between written and earned |
| Loss Ratio | \`incurred_losses_m / earned_premium_m\` | Key profitability metric |
| Loss Severity Index | \`incurred_losses_m * 1000000 / claim_count\` | Average cost per claim |
| Underwriting Margin | \`100 - combined_ratio\` | Profit margin percentage |
| Earned to Written Ratio | \`earned_premium_m / written_premium_m\` | Premium earning rate |
| Expense Ratio | \`(total_expenses / earned_premium_m) * 100\` | Operating cost percentage |

### Aggregation Types

Each measure has a **Default Aggregation** that determines how values are combined:

| Aggregation | Use For | Example |
|-------------|---------|---------|
| **Sum** | Additive values | Premium, losses, claim counts |
| **Average** | Rate-type values | Loss ratio, expense ratio |
| **Count** | Counting records | Policy count, claim count |
| **Min / Max** | Extremes | Minimum premium, maximum loss |
| **Count Distinct** | Unique values | Unique states, unique policyholders |

### Semi-Additive Measures (Time Aggregation)

Some measures don't add up over time — they represent point-in-time snapshots.

| Time Aggregation | Use For | Example |
|------------------|---------|---------|
| **Sum** (default) | Cumulative values | Paid losses, earned premium |
| **Latest** | Point-in-time snapshots | Outstanding reserves, inforce count |
| **First** | Opening balances | Beginning reserves |
| **None** | Non-aggregatable | Ratios, percentages |

**Example**: Loss Ratio calculation mixing aggregation types:
- \`SUM(paid_losses) + LATEST(outstanding_reserves)\` → the query engine automatically uses the correct aggregation for each
- Set "Time Aggregation" to "Latest" on the reserves field in the Semantic Model editor

### Table Calculations

Available in the Visuals tab → Table Calculations section:

| Calculation | Description |
|-------------|-------------|
| Running Total | Cumulative sum down the column |
| % of Total | Each value as a percentage of the column total |
| Rank | Rank from highest to lowest |
| Difference | Change from previous row |
| % Difference | Percentage change from previous row |
| Moving Average | Rolling average (default 3 periods) |
| Year-over-Year | Compare to same period in prior year |
| Prior Period | Show the previous row's value |
| Growth Rate % | Period-over-period growth rate |
| Z-Score | Standard deviations from the mean |
`
  },
  {
    id: 'loss-triangle', title: 'Loss Development Triangle', icon: Table,
    content: `
## Loss Development Triangle

The Loss Triangle is a specialized actuarial tool for analyzing how insurance losses develop over time and projecting ultimate losses.

### What is a Loss Triangle?

A loss triangle shows cumulative losses at successive evaluation points:
- **Rows**: Origin periods (accident year, policy year, etc.)
- **Columns**: Development periods (12 months, 24 months, 36 months, etc.)
- **Values**: Cumulative incurred losses at each stage
- **Diagonal**: The latest evaluation for each origin year

### Creating a Loss Triangle

1. In the dashboard builder, add a visual and set type to **"Triangle"**
2. Drag the **origin field** (e.g., accident_year) to Category
3. Drag the **development period field** (e.g., development_month) to Category
4. Drag the **value field** (e.g., cumulative_incurred) to Values
5. Configure in the config panel: Origin Field, Development Period Field, Tail Factor

### Development Factors

Age-to-age factors (link ratios) measure how losses grow between development periods:

\`Factor = Value at Period N+1 / Value at Period N\`

The triangle engine computes factors using multiple averaging methods:

| Method | Description | Best For |
|--------|-------------|----------|
| **Simple Average** | Arithmetic mean of all factors | General use |
| **Weighted Average** | Volume-weighted (larger years count more) | Default selection |
| **3-Year Average** | Average of last 3 origin years only | Recent trends |
| **5-Year Average** | Average of last 5 origin years only | Medium-term trends |
| **Medial 5×1** | Exclude highest and lowest, average rest | Removing outliers |

### Projection Methods

#### 1. Chain Ladder (Default)
Multiplies the latest diagonal value by remaining development factors:
\`Ultimate = Latest × CDF-to-Ultimate\`

**Best for**: Mature, stable lines of business with predictable development patterns.

#### 2. Bornhuetter-Ferguson
Blends actual development with prior expectations:
\`Ultimate = Actual + (Expected Ultimate × % Unreported)\`

Where:
- Expected Ultimate = Earned Premium × Expected Loss Ratio
- % Unreported = 1 - (1 / CDF)

**Requires**: Earned premium by origin year + Expected loss ratios
**Best for**: Immature accident years where actual data is volatile.

#### 3. Cape Cod (Stanard-Bühlmann)
Like BF, but derives the expected loss ratio from the data itself:
\`ELR = SUM(Actual Losses) / SUM(Earned Premium × % Reported)\`

Then applies the BF formula with this data-derived ELR.

**Requires**: Earned premium by origin year (ELR computed automatically)
**Best for**: When you don't have a strong prior loss ratio assumption.

### Tail Factor
Applied beyond the last observed development period to account for development beyond the triangle's range. Default is 1.0 (no tail). Typical values range from 1.00 to 1.05.

### Reading the Triangle Display

| Element | Color | Meaning |
|---------|-------|---------|
| **Blue highlighted cell** | Primary | Diagonal — latest evaluation |
| **Green italic values** | Emerald | Projected (future development) |
| **Factor rows below** | Muted | Age-to-age development factors |
| **"Selected" row** | Bold primary | The factors used for projection |
| **Ultimate column** | Green | Projected final loss amount |
| **IBNR column** | Amber | Incurred But Not Reported reserve |
| **CDF column** | Blue | Cumulative Development Factor |
`
  },
  {
    id: 'period-comparison', title: 'Period-over-Period Comparison', icon: ArrowLeftRight,
    content: `
## Period-over-Period Comparison

Compare any two time periods side by side with automatic variance calculations.

### Enabling Period Comparison

1. Select a visual in the dashboard builder
2. Open the **Visuals** tab → scroll to **Period Comparison**
3. Toggle it on
4. Select the **Time Field** (e.g., year, quarter, month)
5. Enter **Current** value (e.g., 2024) and **Prior** value (e.g., 2023)

### What You Get

For each measure, four columns are generated:

| Column | Description | Example |
|--------|-------------|---------|
| **Current** | Value in the current period | $631M |
| **Prior** | Value in the prior period | $616M |
| **Variance** | Absolute difference (with trend arrow) | ↑ $15M |
| **Variance %** | Percentage change (colored badge) | +2.45% |

### Color Coding
- **Green** (↑): Positive variance
- **Red** (↓): Negative variance  
- **Gray** (—): No change

### Totals Row
Automatically computed at the bottom, showing aggregate current, prior, variance, and variance %.

### Tips
- Works with any dimension: compare by LOB, state, product, etc.
- The time field is excluded from grouping to enable the comparison
- Supports numeric and string period values
`
  },
  {
    id: 'explore-ai', title: 'AI Explore', icon: Sparkles,
    content: `
## Explore — AI-Native Analytics

Ask questions about your data in natural language. The AI understands your semantic model and generates live charts and tables.

### Getting Started
1. Navigate to **Explore** in the sidebar
2. Select a **Semantic Model** (your data source)
3. Type a question or click a **Suggested Question**

### What You Can Ask

| Query Type | Example |
|------------|---------|
| Trends | "Show written premium trend by year" |
| Comparisons | "Compare loss ratio across lines of business" |
| Top/Bottom N | "Top 3 states by claim count" |
| Breakdowns | "Break down earned premium by LOB and year" |
| KPIs | "What is the total written premium?" |
| Anomalies | "Find unusual patterns in recent claims" |

### AI Persona
Each semantic model can have a custom **AI Persona** that shapes how the AI responds:
- Set in **Models** → Edit Model → **AI Persona / Context**
- Example: "You are a P&C insurance executive advisor. Focus on loss ratios and combined ratios."

### Collaborative Sessions
- Click **Share** to generate a link
- Anyone with the link can view and ask questions
- Real-time: see other participants and typing indicators
- All questions and answers are shared

### Actions on Results
| Action | Description |
|--------|-------------|
| **Save as Insight** | Creates a dashboard/report with the chart |
| **Create Alert** | Sets up a monitoring alert on the metric |
| **Pin Finding** | Saves the insight for later reference |
| **Investigate Why** | Runs root cause analysis |
| **Start Thread** | Creates a discussion for team collaboration |
`
  },
  {
    id: 'visual-library', title: 'Visual Library', icon: Layers,
    content: `
## Visual Library

Create reusable visualizations that can be shared across multiple dashboards and reports.

### Creating a Visual
1. Navigate to **Visual Library** in the sidebar
2. Click **New Visual** → select a semantic model
3. Configure fields, chart type, filters in the **Visual Editor**
4. Click **Save**

### Using Library Visuals in Dashboards
1. Open a dashboard in the builder
2. Click the **Library** tab in the left panel (next to Fields)
3. **Double-click** or **drag** a visual onto the canvas
4. The visual loads with all its field mappings pre-configured

### Linked Visuals
- Library visuals placed on dashboards maintain a **link**
- Edit the library visual → changes propagate to **all dashboards** using it
- Green "linked" badge shows on linked visuals in the builder

### Promote to Library
- Right-click any inline visual on a dashboard → **Save to Library**
- Creates a library visual and converts the inline visual to a linked reference

### Top N / Bottom N
Available on any visual in the config panel:
- Toggle **Top / Bottom N**
- Select **Top** or **Bottom**, enter a count (e.g., 3)
- Choose which measure to sort by
`
  },
  {
    id: 'semantic-models', title: 'Semantic Models', icon: GitBranch,
    content: `
## Semantic Models

Semantic models define the business meaning of your data — dimensions, measures, relationships, and glossary terms.

### Key Concepts

| Concept | Description |
|---------|-------------|
| **Entity** | A data table (e.g., Premium Transactions, Claims) |
| **Attribute** | A column — either a Dimension or Measure |
| **Dimension** | A field you group by (year, LOB, state) |
| **Measure** | A field you aggregate (premium, losses, counts) |
| **Relationship** | A join between two entities |
| **Calculated Measure** | A formula-based metric |
| **Glossary** | Business definitions for terms |

### Model-Specific Glossary
- Each model has its own glossary (Models → select model → Glossary section)
- Click the sparkle icon to **auto-generate** glossary entries from field names
- The AI Explore uses these definitions when answering questions

### Time Aggregation
Set on measure fields to control how they aggregate across time:
- **Sum**: Standard additive (premium, losses)
- **Latest**: Point-in-time snapshot (outstanding reserves, inforce count)
- **First**: Opening balance
- **None**: Non-aggregatable (ratios)

### Data Categories, PII & Auto Geo Maps
Each field carries a **Data Category** (in the field editor, or a collapsed-row badge) that goes beyond dimension/measure:
- **Geographic** — State / County / City / Postal Code / Latitude / Longitude. When you add a geographic field to a chart, the visual **auto-switches to a map** (US-state choropleth, or a point map for lat/long). AI Explore also renders a map automatically when you ask a geo question ("total premium by state").
- **Date** — flags temporal fields for date-aware analysis.
- **PII** — mark a field as PII (or run **Auto-Detect PII** to have the AI flag names/emails/SSNs/etc.). PII values are masked for non-admins and embed sessions; access is auditable.

Use **Bulk Classify** in the toolbar to set categories or PII on many fields at once.

### Importing a Model
- **Import Model JSON** brings in an external ER-model / warehouse export: tables → entities, columns → attributes, relationships → joins. Source categories map to semantic types (identifier/hidden → hidden, measure → measure, date/geographic/other → dimension), and geographic/date/PII hints carry over automatically. Definition only — connect a data source to query real rows.

## Aggregate Awareness & Governance

Aggregates are smaller, pre-summed tables that answer common queries **10–1000× faster**. INZARA's router automatically sends each query to the cheapest table that can answer it correctly, and the **Aggregate Governance** page (Models → open a model → Aggregates, or the governance page) keeps those bindings trustworthy.

### Authoring an Aggregate (Mapping Wizard)
- Open the **Aggregate Wizard** to declare a summary table: pick the canonical fact, its grain (the columns it rolls up to), and a **measure map**.
- **Conditional / pre-baked aggregates** are supported: e.g. \`written_premium = SUM(amount) WHERE amount_type_code='premium'\`. Declare the baked predicate and any **decomposition dimensions** so the router knows the summary already filtered on them.
- **Pre-split routing**: a filter on a baked dimension (e.g. \`new_or_renewal='new'\`) can be served from a matching pre-split column instead of falling back to the raw transaction table.

### Multi-Summary Best-Fit routing
When several summary tables could answer a query, the router picks the **cheapest compatible** one, disqualifies tables whose grain can't serve the requested dimensions, and breaks cost ties deterministically. The governance page's decision timeline and query mix show what the router chose and why.

### Reconciliation & Certification
- **Reconcile** compares each summary column against the canonical recomputed with the same baked predicate — per measure and per period — so you see exactly what matches before trusting it.
- **Certify** a binding once it reconciles cleanly; certified results drop the "unverified aggregate" badge in Query Details.
- **Auto-certify** (per binding): a clean reconcile certifies the binding automatically, no manual click.
- **Recertify** (one tap): reconciles **and** certifies a drifted/unverified binding in a single step; if it still drifts it won't certify, and shows you what's off.
- **Reconcile all**: reconcile every aggregate in the model on demand from the governance page.

### Automatic reconcile schedule
- Each aggregate has a **reconcile cadence**: **Hourly**, **Nightly** (default), or **Manual** (opt out of the automatic sweep). The scheduler reconciles due bindings, auto-certifies clean ones, and de-certifies any that drift.

### Drift alerts for stewards
- When a scheduled reconcile finds a summary column diverging from transaction truth, INZARA notifies your **data stewards** (anyone who can certify models) in-app — the notification bell shows an unread count.
- **Drift Digest frequency** (Tenant Defaults): choose **Hourly** (alert as soon as a sweep finds drift) or **Daily** (one roll-up per day) for the whole tenant.
- **Snooze**: mute a specific aggregate's drift alerts for 24h while you fix the source. Snoozed bindings still reconcile and still lose certification on drift — they're just left out of the steward digest until the mute expires (**unmute** anytime).

### Policies & cost
- **Tenant Defaults / model policy** control hybrid-union, sketch tolerance, drift threshold, and fallback-on-drift — with per-key inheritance (built-in → tenant → model).
- The **Cost & ROI** panel shows storage, compute spend, estimated time saved, and net ROI per aggregate so you can prove the value.
- **Suggested Aggregates** proposes new summaries derived from the queries that recently missed the cache.
`
  },
  {
    id: 'ai-visual', title: 'AI Visual Builder', icon: Wand2,
    content: `
## AI Visual Builder

Create and modify visualizations using natural language descriptions. The AI understands your semantic model and configures the optimal chart type, fields, and settings.

### Using AI Build in the Visual Editor

1. Open a visual in the **Visual Editor** (from Visual Library)
2. Type a description in the **AI Build bar** at the top
3. Press Enter or click **AI Build**
4. The AI configures: chart type, dimensions, measures, color/series, Top N

### Example Prompts

| Prompt | Result |
|--------|--------|
| "bar chart of premium by LOB" | Bar chart, line_of_business dimension, written_premium_m measure |
| "line chart showing trend over years with colors for each LOB" | Line chart, year x-axis, LOB as color series |
| "top 5 states by claim count" | Bar chart, state dimension, claim_count measure, Top 5 |
| "pie chart of premium distribution" | Pie chart, line_of_business, written_premium_m |
| "change to a table" | Modifies existing visual to table type |

### How It Works
- The AI reads your semantic model's field names, descriptions, and types
- It selects the most appropriate chart type for the data
- It suggests **alternative chart types** you can switch to with one click
- It provides an **explanation** of why these fields and chart type were chosen
- For modifications, pass the current config and describe what to change

### Tips
- Be specific about what you want to see: "premium by year" is better than "show me data"
- Mention "top N" or "bottom N" for automatic filtering
- Mention "colors by" or "split by" for multi-series charts
- The AI uses the model's **AI Persona** for domain-specific understanding
`
  },
  {
    id: 'ai-dashboard', title: 'AI Dashboard Composer', icon: Sparkles,
    content: `
## AI Dashboard Composer

Generate complete multi-visual dashboards from a single description. The AI creates KPIs, charts, and tables with professional layout.

### Creating an AI Dashboard

1. Go to **Insights** page
2. Click the **AI Build** button (sparkle icon)
3. Select **Dashboard** mode (default)
4. Choose a **Semantic Model**
5. Describe the dashboard you want
6. Click **Build Dashboard with AI**

### What the AI Creates

The AI generates 4-8 visuals with smart layout:

| Position | Visual Type | Example |
|----------|-------------|---------|
| **Top row** | KPI cards | Total Premium, Loss Ratio, Combined Ratio |
| **Middle** | Large trend charts | Premium by Year and LOB (full width) |
| **Middle** | Medium comparison charts | Top 5 States by Claims (half width) |
| **Bottom** | Detail tables | Full performance breakdown |

### Example Prompts

- "Executive summary with KPIs and trends"
- "Claims analysis by LOB and state with a detail table"
- "Premium performance with year-over-year comparison"
- "Underwriting profitability breakdown with combined ratio trends"

### Preset Suggestions
Click the suggestion chips below the text area for common dashboard templates.

### After Generation
The dashboard opens in the builder where you can:
- Rearrange visuals by dragging
- Modify individual visuals (change chart type, add fields)
- Add additional visuals from the Library tab
- Add slicers and filters
`
  },
  {
    id: 'ai-report', title: 'AI Report Generator', icon: FileText,
    content: `
## AI Report Generator

Generate professional multi-page pixel-perfect reports with narratives, charts, tables, and professional layout.

### Creating an AI Report

1. Go to **Insights** page
2. Click **AI Build** button
3. Switch to **Pixel-Perfect Report** mode
4. Choose a Semantic Model
5. Describe the report
6. Click **Build Report with AI**

### What the AI Creates

| Element | Description |
|---------|-------------|
| **Title page** | Report name, executive summary narrative |
| **KPI section** | Key metrics in card format |
| **Charts** | Trend charts, comparison charts with precise positioning |
| **Narrative text** | AI-written analytical paragraphs referencing actual data |
| **Tables** | Detail breakdowns with proper column widths |
| **Headers/Footers** | Report title, page numbers, date stamps |

### Multi-Page Support
The AI distributes content across pages logically:
- Page 1: Executive summary with narrative and KPIs
- Page 2-3: Analysis sections with charts and commentary
- Final page: Detail appendix with data tables

### Example Prompts

- "Quarterly board pack with executive summary, premium analysis, claims breakdown, and appendix"
- "Loss run report grouped by LOB with subtotals"
- "Premium analysis report with year-over-year comparison"
- "Claims analysis with reserves breakdown by state"

### Report Features
- **Pixel-perfect layout**: Visuals positioned at exact coordinates
- **AI narratives**: Professional analytical text blocks
- **Section headers**: Auto-generated with styling
- **PDF ready**: Designed for print/export with proper page breaks
`
  },
  {
    id: 'ai-insights', title: 'AI Proactive Insights', icon: Brain,
    content: `
## AI Proactive Insights

The platform can automatically analyze your data for anomalies, notable changes, and actionable insights.

### Anomaly Scanning

Available on the **Home** page under "Data Insights":
1. Click **Scan Now** to analyze your primary data model
2. The AI queries actual data summaries (totals, time trends, category breakdowns)
3. Returns 3-6 insights ranked by severity

### Insight Severity Levels

| Severity | Indicator | Meaning |
|----------|-----------|---------|
| **Critical** | Red dot | Significant negative change requiring immediate attention |
| **Warning** | Amber dot | Notable deviation from expected patterns |
| **Info** | Blue dot | Interesting trend or data point worth noting |
| **Positive** | Green dot | Good news — improvement or positive trend |

### What Gets Analyzed
- **Overall totals**: Key metrics at the aggregate level
- **Time trends**: Changes across periods (year, month, quarter)
- **Category breakdowns**: Performance by business dimensions (LOB, state, product)

### Clicking an Insight
Each insight includes a **suggested query** — clicking it opens Explore with that question pre-filled, so you can drill deeper.

### Weekly Digest
Generate a professional email summary of your data insights:
- Available via API: POST /api/ai/weekly-digest
- Includes all insights formatted as a professional email
- Can be integrated with scheduled email delivery

### Tips
- Run scans periodically to catch emerging trends
- The AI uses your model's **AI Persona** for domain-specific analysis
- Insights are cached — the Home page shows the most recent scan
- Click any insight to jump to Explore for deeper investigation
`
  },
  {
    id: 'tenant-admin', title: 'Tenant Management & AI Credits', icon: Building2,
    content: `
## Tenant Management & AI Credits

### Multi-Tenancy
The platform supports multiple organizations (tenants), each with isolated data, users, reports, and models.

### Tenant Management (Super Admin)
Navigate to **Admin → Tenants** to manage organizations:

| Action | Description |
|--------|-------------|
| **Create Tenant** | Set name, domain, plan (Starter/Standard/Enterprise), max users |
| **View Details** | User count, report count, model count, AI usage stats |
| **Add Users** | Create users with email, name, password, role (Viewer/Editor/Admin) |
| **Set AI Credits** | Configure monthly AI call limits per tenant |
| **Update Plan** | Change plan, features, status |

### AI Credit Tracking
Every AI call is tracked per tenant:

| Metric | Description |
|--------|-------------|
| **Total Calls** | Number of AI API calls this month |
| **Tokens Used** | Estimated input + output tokens |
| **Estimated Cost** | Based on GPT-4o pricing |
| **By Feature** | Breakdown: Explore, Build Visual, Build Dashboard, Build Report, Anomaly Scan |

### Credit Limits
- Set a monthly AI credit limit per tenant
- **Warning banner** appears when 80% consumed
- **Blocked** at 100% with message to contact admin
- Super admins can adjust limits at any time

### Roles

| Role | Access |
|------|--------|
| **Super Admin** | Manage all tenants, users, credits. System-level access |
| **Admin** | Manage own tenant's users, reports, models. Full feature access |
| **Editor** | Create/edit reports, visuals, models. No admin access |
| **Viewer** | View dashboards and reports. No edit access |

### Row-Level Security
Restrict data visibility per role:
1. Go to **Admin Console → RLS Policies**
2. Create a policy: select model, field, operator, value (static or user attribute)
3. Assign to roles — users in those roles only see matching data
`
  },

  // ─── New per-screen reference docs ─────────────────────────────────────
  {
    id: 'marketplace', title: 'Marketplace & Extensions', icon: Layers,
    content: `
## Marketplace & Extensions

Extend INZARA with certified and community extensions — visualizations, themes, data connectors, and automation workflows that wire into real platform behavior.

### Extension kinds

| Kind | Example | Effect |
|------|---------|--------|
| **Visualization** | Sankey Plus, Carbon Footprint KPIs | Adds chart types / KPI packs |
| **Theme** | AMOLED Dark | Adds CSS variables, restyles the app |
| **Connector** | ClickHouse | Registers a new data source type |
| **Workflow** | PagerDuty Alert Routing | Routes anomaly alerts to external systems |

### Installing
1. Go to **Marketplace** in the sidebar
2. Pick an extension → **Install**
3. Fill any required config (host, API key, etc.) and toggle **Enabled**

Installs are per-tenant. Admins can update config, disable, or uninstall at any time. Built-in extensions can be disabled but not removed from the catalog.

### One-click sample datasets
The **Try a sample dataset** banner runs guided wizards that create a working data source + semantic model in seconds. Each card shows a "rows · engine" badge so you know what you're getting before you click.
`
  },
  {
    id: 'sample-datasets', title: 'Sample Datasets (one-click)', icon: Database,
    content: `
## Sample Datasets

Pre-configured, one-click wizards that stand up a real, queryable model — perfect for trials, sales demos, and kicking the tires on a new engine.

| Dataset | Engine | What you get |
|---------|--------|--------------|
| **NYC Taxi Trips** | DuckDB | A 50k-row slice of the public NYC TLC yellow-taxi data, downloaded once and cached locally for sub-second queries. No credentials needed. |
| **NBA Player Stats** | MongoDB | 12 top scorers from the 2023-24 season — great for testing dimensions × measures. |
| **ClickHouse Playground** | ClickHouse | Connects to the public ClickHouse demo cluster to prove SQL push-down end-to-end. |
| **Carbon Footprint KPIs** | Extension | Installs the Carbon Footprint extension + 3 starter sustainability KPIs. |

### How it works
Each wizard idempotently creates a data source, a semantic model, and its entities, then drops you straight into **Explore** with the model preselected. Re-running a wizard is safe — it reuses the cached setup.

### First-run experience
New tenants land on this gallery automatically. Existing users can revisit it anytime via **Profile → Reset to first-run view**.
`
  },
  {
    id: 'apps-workspaces', title: 'Apps & Workspaces', icon: Briefcase,
    content: `
## Apps & Workspaces

Bundle dashboards, reports, and semantic models into a versioned **App** and publish it to a specific audience — INZARA's answer to Power BI Apps / Tableau Projects.

### Lifecycle
1. **Create** a workspace and add content (dashboards, reports, models)
2. **Publish** — freezes the current content into a numbered version (v1, v2, …)
3. Recipients open the **published** version; you keep editing the draft safely

### Why use Apps
- Distribute a curated analytics experience without exposing your working drafts
- Frozen versions mean recipients always see a stable, consistent view
- Re-publish to push an update; previous versions stay traceable
`
  },
  {
    id: 'tableau-import', title: 'Tableau Import', icon: FileText,
    content: `
## Tableau Import

Migrate from Tableau by importing workbook and data-source files, then rebuilding them as native INZARA dashboards.

### Supported files
\`.twb\`, \`.twbx\`, \`.tds\`, \`.tdsx\`

### Steps
1. Go to **Models** → **Import** → **Tableau**
2. Upload the file — INZARA extracts datasources, fields, and metadata
3. Review the **Import Summary** (datasources, columns, build version detected)
4. Click **Rebuild as INZARA Dashboard** to scaffold a native equivalent

### Notes
- Field types and calculated fields are mapped to INZARA's semantic vocabulary where possible
- Anything that can't be auto-mapped is surfaced so you can finish it manually
`
  },
  {
    id: 'impact-analysis', title: 'Lineage & Impact Analysis', icon: Network,
    content: `
## Cross-Asset Lineage & Impact Analysis

A platform-wide dependency graph connecting every asset: data source → prep pipeline → semantic model → visual → dashboard/report → alert/schedule.

### Cross-asset lineage
Open **Workspace → Lineage** to walk the full graph. Click any node to see its direct parents and children, and trace a number all the way back to its source.

### Impact analysis (before you break something)
When you delete or change an asset, INZARA computes everything downstream that depends on it:
- **Deletion warnings** list the dashboards, reports, alerts, and schedules that would be affected
- Use it to answer "if I change this source column, what breaks?" before you hit save

### Endorsement-aware
Endorsed (trusted) sources are highlighted across the platform — the Insights list can filter by endorsement, and the AI Copilot model picker prioritizes endorsed sources.
`
  },

  {
    id: 'home', title: 'Home Dashboard', icon: Home,
    content: `
## Home Dashboard

The landing page when you sign in. At a glance you see:

| Panel | What it shows |
|-------|---------------|
| **Quick Start** | One-click flow to upload a CSV and get AI-built dashboards in under a minute |
| **Recent Work** | The last reports, dashboards, and visuals you opened |
| **Favorites** | Pinned reports/dashboards for fast access |
| **Data Insights** | Anomaly-scan highlights auto-generated for your models |
| **Quick Insights** | Proactive AI highlights the system thinks you'd want to see |
| **Impersonation Sessions** | Super-admin activity showing tenants currently being impersonated |

**Tips**
- Pin anything you use daily via the star icon on reports, dashboards, and models
- The search bar at the top ("Ask AI") accepts natural-language questions against any model
`
  },
  {
    id: 'quickstart', title: 'Quick Start (CSV → Dashboards)', icon: Zap,
    content: `
## Quick Start

A 4-step guided flow that takes a CSV and builds AI-generated semantic model + three dashboards (Executive, Trends, Deep Dive).

### Steps
1. **Upload** — drag/drop a CSV (UTF-8 or UTF-8-BOM; single or multi-sheet Excel supported)
2. **Tell Us More** — answer the questionnaire (business, industry, KPIs, time period) so the AI tailors the output
3. **AI Building** — the platform imports the data, detects types, generates calculated measures, and writes the dashboards. AI narratives are pre-rendered so the dashboard opens instantly
4. **Ready** — click **View Dashboard** to jump straight into the Executive Summary

### What you get
- **Semantic model** with 5-10 attributes, 3-5 calculated measures, types auto-reconciled from your data
- **3 dashboards** — Executive Summary (KPIs + gauge + combo chart), Trends & Forecast (line/area with forecasting), Deep Dive (heatmap, treemap, pivots)
- **Data Quality banner** — if the AI reconciled any column types after seeing your data, a banner lists the corrections with per-column from → to chips and a "Review in Model" deep link

### Troubleshooting
- **KPIs show "—"**: a calculated measure's formula probably references a non-numeric column. Open the model and check the measure's formula / aggregation.
- **All points at y=0**: usually a BOM-encoded or string-typed numeric column. Re-upload or run the type reconciliation from the Data Quality banner.
`
  },
  {
    id: 'reports', title: 'Insights / Reports list', icon: List,
    content: `
## Insights & Reports

Browse every dashboard, report, and folder your tenant owns. You can group them into folders, share them, duplicate, export, or schedule.

### Actions per row
| Action | Description |
|--------|-------------|
| **Open** | Launches the appropriate viewer — dashboard, table, or pixel-perfect report |
| **Edit** | Opens the Report Builder |
| **Duplicate** | Creates a copy in the same folder |
| **Share** | Generates a sharable link or embed token |
| **Schedule** | Opens the Schedule creator for this report |
| **Export** | One-off CSV / PDF download |

### Folders & permissions
- Drag reports into folders
- Set folder-level permissions to control which roles can open the reports inside
- Share a folder with a user/role to grant access to everything inside

### Favourites
Star any row to pin it to your Home dashboard.
`
  },
  {
    id: 'report-builder', title: 'Report & Dashboard Builder', icon: FileText,
    content: `
## Report & Dashboard Builder

The canvas for building dashboards (free-form) and pixel-perfect reports (page-based).

### Left Panel
| Tab | Purpose |
|-----|---------|
| **Fields** | Drag dimensions and measures onto shelves |
| **Library** | Reusable visuals from the Visual Library |
| **Pages** | Add/reorder pages (pixel-perfect mode) |

### Middle Panel
- **Canvas** — drop visuals, resize, snap to grid, use alignment guides
- **Toolbar** — add a new visual: chart, AI text, text block, annotation, image, rich text, shape

### Right Panel
| Tab | Purpose |
|-----|---------|
| **Data** | Configure the selected visual's fields, aggregations, sort |
| **Visuals** | Chart type + chart-specific config (e.g. gauge ranges, combo axes) |
| **Advanced** | What-if parameters, conditional rules, reference lines |

### Modes
- **Dashboard mode** — free-form, responsive grid
- **Report mode** — page-based, print-ready with header/footer zones

### Undo / Redo
Cmd/Ctrl + Z and Shift + Cmd/Ctrl + Z on the canvas.

### Save
**Save** writes a new version. The version history is browsable via the clock icon in the header.
`
  },
  {
    id: 'dashboard-view', title: 'Dashboard Viewer', icon: Gauge,
    content: `
## Dashboard Viewer

Read-only view of a dashboard with live interactivity.

### Features
- **Global filter bar** at the top — click a slicer to filter every visual on the page
- **Drill-through** — click a bar/slice to deep-dive
- **Click-to-filter** — clicking a visual filters the rest of the dashboard
- **AI narratives** — pre-rendered when the dashboard loads
- **Data Quality banner** — if the underlying model auto-corrected column types, you'll see the details here with a dismiss-per-model option

### Sharing
Click **Share** in the header to get a view-only link, an embed token, or a scheduled-delivery setup.

### Print / Export
Use the export button for one-off PDF or to send the current view via email (if SendGrid is configured on your tenant).
`
  },
  {
    id: 'data-sources', title: 'Data Sources', icon: Database,
    content: `
## Data Sources

Connect the platform to databases, warehouses, spreadsheets, and APIs.

### Supported connectors
| Type | Notes |
|------|-------|
| **Snowflake** | Account, warehouse, database, role |
| **Redshift** | Standard PG-wire connection |
| **PostgreSQL / MySQL / SQL Server** | Standard DSN connection |
| **MongoDB** | Connection string + DB name |
| **ClickHouse** | Native OLAP adapter — SQL push-down for billion-row tables |
| **DuckDB** | Embedded analytical engine for local/file-based data — sub-second queries |
| **CSV / Excel** | Upload once or set up periodic refresh from a URL |
| **REST API** | Declare endpoint, headers, JSON path — platform auto-schemas the response |

### Test connection
Every connector has a **Test** button that runs a trivial query before you save — credential issues surface immediately.

### Secrets handling
Passwords / API keys are encrypted at rest. Never committed to logs or audit trails.

### Refresh cadence
Per-source refresh interval (hourly / daily / manual). Configure under the source's **Advanced** tab.
`
  },
  {
    id: 'data-prep', title: 'Data Prep Pipelines', icon: Workflow,
    content: `
## Data Prep Pipelines

Build visual node-based pipelines that clean, join, and reshape raw tables before the semantic layer sees them.

### Step types
| Step | Purpose |
|------|---------|
| **Source** | Pick a table / collection |
| **Filter** | One or more AND/OR conditions |
| **Rename** | Rename columns for downstream clarity |
| **Cast** | Change a column's data type |
| **Derive** | New column from an expression (maths + whitelisted funcs) |
| **Sort** | Order by one or more columns |
| **Limit** | Top-N rows |
| **Fill null** | Default value for missing cells |
| **Union** | Append another pipeline's output |
| **Join** | Inner/left/right join to another table |
| **Pivot** | Reshape rows into columns (long → wide) |

### Canvas
Nodes flow top-to-bottom. Click any node to edit it in the right panel. The minimap (bottom-right) shows the full pipeline and lets you jump by clicking a node pill.

### Preview
Run any step's output to see the first 100 rows before you save.

### Save & version
Every save creates a new pipeline version. Previous versions stay browsable in the version history.
`
  },
  {
    id: 'glossary', title: 'Business Glossary', icon: BookMarked,
    content: `
## Business Glossary

Central, AI-aware business-term definitions. Terms link to fields in your semantic models so the AI Explore and anyone reading a dashboard knows exactly what each number means.

### Per-model vs tenant-wide
- **Tenant-wide glossary** — the top-level Glossary page. Shared across every model.
- **Model-specific glossary** — inside each semantic model editor, overrides and augments the tenant-wide one for that model.

### Auto-generate
Click the sparkle icon inside a model's Glossary panel to have the AI read your field names and propose definitions. Review and save.

### How it helps AI
When someone asks the AI a question, your definitions inject into the prompt so the AI uses the right math (e.g. "Loss Ratio is incurred_losses / earned_premium") rather than guessing.
`
  },
  {
    id: 'alerts', title: 'Alerts', icon: Bell,
    content: `
## Alerts

Monitor a metric and get notified by email / Slack / Teams when it breaches a threshold.

### Creating an alert
1. **Alerts → New Alert**
2. Pick a semantic model and measure
3. Choose condition: simple (\`measure > threshold\`) or advanced (nested AND/OR groups)
4. Pick cadence (every run, every N hours, only weekdays)
5. Add recipients

### Advanced conditions
Click **Advanced mode** to build nested groups, e.g. *(loss_ratio > 0.7 AND region = 'North') OR (claim_count > 1000)*.

### From a visual
Right-click any chart → **Create alert from this** to pre-fill the builder with the current measure and filters.

### History
Every alert has a history tab showing the last N evaluations: value, threshold, fired / ok, recipients who received the notification.
`
  },
  {
    id: 'schedules', title: 'Schedules & Bursting', icon: CalendarClock,
    content: `
## Schedules & Bursting

Send a report or dashboard on a cadence to one recipient list, or **burst** it into per-segment filtered copies delivered to different people.

### Basic schedule
1. **Schedules → New Schedule**
2. Pick a report / dashboard
3. Choose format (CSV, data PDF, dashboard PDF, or combos)
4. Set cadence (daily / weekly / monthly)
5. Add recipients

### Report bursting
1. Toggle **Enable bursting**
2. Pick a burst dimension (e.g., \`region\`)
3. Click **Discover segments** — auto-populates every distinct value
4. Assign each segment to one or more email recipients
5. (Optional) use \`*\` as a catch-all segment with a fallback email

### Preview before sending
**Preview segment** runs the filtered query for one segment and shows the data (CSV) or rendered output (dashboard PDF) so you can sanity-check before enabling.

### Dashboard PDFs
Take longer than data-PDFs (~30-45s per segment) because they're rendered in a headless Chromium. Runs in parallel with a semaphore cap to protect memory.
`
  },
  {
    id: 'anomaly-scans', title: 'Anomaly Scans', icon: AlertTriangle,
    content: `
## Anomaly Scans

AI-driven proactive monitoring of every measure in every published model. Finds unusual patterns you didn't think to ask about.

### What it detects
- Outliers (values ≥ 3σ from the mean for the dimension)
- Trend breaks (sudden slope changes)
- Seasonality anomalies (value way off its historical same-period)
- Correlation breakdowns (two measures usually move together — this period they didn't)

### Schedule
Enable per-model scans (every N hours) or rely on the platform-wide 24-hour default for any tenant with \`auto_anomaly_scan\` turned on.

### Digest email
At the end of every scan, an email / Slack digest goes to the tenant owner summarising the top anomalies with a one-click "Investigate" deep link.
`
  },
  {
    id: 'messaging', title: 'Messaging, Object Chat & Mentions', icon: MessageSquare,
    content: `
## Messaging & Collaboration

Real-time, **self-hosted** collaboration built right into the platform — 1:1 direct messages, group DMs, and chat attached to any object. This replaces the old Discussions.

![The Messages hub — direct messages, group DMs and object-attached conversations with live presence](/help-assets/messaging.png)

### The Messages hub
Open **Messages** (\`/threads\`) to see all your conversations, workspace presence (who's online / away), and unread counts. Start a **direct message** or a **group DM** with anyone in your tenant.

### Object chat — discuss where you work
Every report, dashboard, AI Insight, and KPI has its own attached conversation:
- **Reports & dashboards** — open the **Chat** tab in the right panel.
- **AI Insights** — click **Start thread** on any insight card.
- **KPI Monitor** — click **Discuss** on any KPI card.

![The Chat tab on a report opens the object-attached conversation right beside the data](/help-assets/object-chat.png)

### Rich conversations
- **@mentions** — type \`@\` to notify a teammate.
- **Reactions, threads & pinned messages** keep long conversations organized.
- **Live presence & unread badges** update in real time.

### Mention bridge (Slack / Teams / email)
When you \`@\`-mention someone or DM them, they're notified **in-app** and — if your tenant has them configured — in **Slack**, **Microsoft Teams**, and **email**. Delivery is best-effort: unconfigured channels are simply skipped.

> Messaging is entirely self-hosted over a WebSocket — no third-party chat service or vendor keys. Everything stays inside your tenant.
`
  },
  {
    id: 'presence-follow', title: 'Live Presence, Share View & Follow', icon: Gauge,
    content: `
## Live Presence, Share View & Follow

Review together in real time — while every person always sees data through **their own** permissions and row/column security.

![Following a teammate: a live banner syncs your view to theirs, with the Share View button and shared-view links in chat](/help-assets/presence-follow.png)

### "N viewing now"
When teammates open the same report or dashboard, their avatars appear in the header ("**N viewing**"). It's an instant signal that you're looking at the same thing.

### Follow mode
Click a viewer's avatar to **follow** them. Their navigation and filter changes stream to you live, and a banner shows *"Following {name} — your view syncs to theirs"*. Click **Stop following** anytime.

### Share View
Click **Share view** to drop your exact **route, filters, and selection** into the object chat as an **"Open this view"** link. When a teammate opens it, they land on precisely what you were looking at.

> **Security first:** Follow and Share View send *navigation and context only* — never raw data or pixels. The recipient's browser re-runs the queries under **their** permissions, so nobody ever sees data they aren't entitled to.
`
  },
  {
    id: 'co-editing', title: 'Live Co-Editing (Dashboard Builder)', icon: Layers,
    content: `
## Live Co-Editing

Build a dashboard together in real time in the **Dashboard Builder** — self-hosted, with no merge conflicts.

![Two people co-editing a dashboard: member avatars, "X is editing", Request / Take control, and a live name-tagged cursor](/help-assets/co-editing.png)

### Who's here & who's driving
Open the builder on a **saved** dashboard and everyone editing appears as avatars in the header. The current editor is ringed and labeled — you'll see either **"You have edit control"** or **"{name} is editing"**.

### Edit control (request / take / grant)
Only one person edits at a time, so changes never collide:
- The current editor's changes (add/move/resize tiles, settings, name, tabs) sync live to everyone.
- Non-editors are **read-only** — the widget palette, Save, and settings are disabled.
- Ask for the pen with **Request** (the editor gets a toast to *Give control*) or **Take control** for an immediate, transparent handoff.

### Shared cursors
Everyone's cursor is shown live with a **colored name tag**, so you can point and gesture as you build.

> Co-editing is self-hosted over the same messaging WebSocket. New, unsaved dashboards are simply edited solo until you save them.
`
  },
  {
    id: 'access-control', title: 'Access Control (RBAC + RLS)', icon: ShieldCheck,
    content: `
## Access Control

Two complementary layers: **role-based access control** (what a user can *do*) and **row-level security** (what data a user can *see*).

### Roles
| Role | Typical use |
|------|-------------|
| **Viewer** | Read-only, dashboards and reports only |
| **Editor** | Build/edit reports, visuals, models |
| **Admin** | Manage own tenant — users, integrations, roles |
| **Super Admin** | Platform-level (tenant lifecycle, deploy status) |

Custom roles can be created with fine-grained permission sets.

### Row-Level Security (RLS)
Policies filter rows based on the current user's attributes. E.g. *"Users with \`region = North\` attribute only see rows where \`region = North\`"*.

1. **Access Control → RLS Policies → New**
2. Pick the model + field + operator + value source (static or user attribute)
3. Assign to roles — users in those roles see the filter applied on every query
`
  },
  {
    id: 'activity', title: 'Activity / Audit Log', icon: Activity,
    content: `
## Activity & Audit Log

Append-only log of every meaningful action: logins, report edits, schedule creations, tenant-lifecycle changes, role changes, AI usage.

### Filters
Filter by actor (user), action type, resource, tenant, date range.

### Compliance
Audit logs are *never deleted* when a tenant is hard-deleted. They're retained indefinitely for compliance / forensics. Only explicit log retention policies can trim them.

### Exporting
Any filtered view can be exported to CSV.
`
  },
  {
    id: 'settings', title: 'Settings (per-tenant)', icon: SettingsIcon,
    content: `
## Settings

Per-tenant configuration. Each tab is independently saveable.

| Tab | What you configure |
|-----|---------------------|
| **Profile** | Tenant name, logo, branding colours |
| **Notifications** | Email (SendGrid), Slack, Teams integrations with test buttons + setup guides |
| **Embed & API** | API keys, embed tokens, allowed origins |
| **Billing** | Plan, usage, renewal date (Enterprise only) |

### Email provider toggle
Under Notifications → **Email**, choose:
- **Use INZARA** — emails sent via the platform's managed SendGrid (recommended)
- **Own SendGrid** — paste your API key + sender — full control / white-label

### Slack interactivity
If you add a Slack **Signing Secret** under the Slack section, the signup-approval notifications arrive with interactive **Approve / Decline** buttons. Configure your Slack app's *Interactivity Request URL* to \`/api/integrations/slack/interactivity\`.
`
  },
  {
    id: 'kpi-monitor', title: 'KPI Monitor', icon: Gauge,
    content: `
## KPI Monitor

A consolidated page that pins every critical KPI across every model and every dashboard into one scorecard with period-over-period deltas.

### What you see
- KPI name, current value, previous-period value, absolute Δ, % Δ
- Traffic-light colour (green / amber / red) tied to each KPI's target thresholds
- One-click **drill** to the dashboard the KPI comes from

### Configuring a KPI
Inside a dashboard, right-click a KPI card → **Pin to KPI Monitor** → set target thresholds.
`
  },
  {
    id: 'lineage', title: 'Data Lineage', icon: Network,
    content: `
## Data Lineage

Interactive graph showing how data flows from source → prep pipeline → semantic model → report/dashboard.

### Use cases
- **Impact analysis** — "if I change this source column, what breaks?"
- **Debugging** — "why is this KPI wrong? Trace it back to the source"
- **Documentation** — show auditors the full data path

### Controls
- Filter by model, pipeline, or report
- Click a node to see its definition + direct parents/children
- Export the graph to PNG / SVG
`
  },
  {
    id: 'embed-manager', title: 'Embed SDK & Developer APIs', icon: CodeIcon,
    content: `
## Embed SDK & Developer APIs

Drop live INZARA content — dashboards, reports, KPI monitor, Insights, AI Explore — straight into your own app with a short-lived, workspace-scoped session token.

### Two SDKs — pick your stack
- **[\`@inzara/embed-react\`](https://www.npmjs.com/package/@inzara/embed-react) (v0.5)** — first-class React \`<InzaraEmbed />\` component with hooks (\`useInzaraEvents\`), forwardRef handle (\`applyFilter\`, \`clearFilter\`, \`ping\`), and silent auto-refresh built in.
- **[\`@inzara/embed-js\`](https://www.npmjs.com/package/@inzara/embed-js) (v0.3)** — zero-dependency vanilla JS \`mount()\` helper for Vue, Angular, Svelte, plain HTML — anywhere React doesn't reach.

Both share the exact same postMessage protocol, so hosts can switch or mix without changing their backend.

### 60-second live playground
Head to **[/embed-playground](/embed-playground)** to mint a real session in the browser, paste the code snippet into your app, and watch INZARA render inside your host page. No install, no build step.

### Embed surfaces
The platform exposes a full catalog of embeddable surfaces. Fetch the live list any time from **[/api/mgmt/v1/embed/surfaces](/api/mgmt/v1/embed/surfaces)**.

| Resource | Iframe path | What it renders |
|----------|-------------|-----------------|
| \`insights\` | \`/embed/v2/insights\` | Merged reports + dashboards feed with kind filters |
| \`kpi-monitor\` | \`/embed/v2/kpi-monitor\` | KPI cards with status banding + progress bars |
| \`dashboard\` / \`report\` | \`/embed/v2/view/:resource/:id\` | Full grid canvas with widgets, drag-to-resize authoring, version history, team comments, @-mentions |
| \`explore\` | \`/embed/v2/explore\` | AI Explore — chat-style NL analytics, budget-guarded via the workspace AI cap |
| \`activity\` | \`/embed/v2/workspace/activity\` | Cross-dashboard workspace activity feed |
| \`investigate\` | dedicated embed page | Root-cause investigation runs |
| \`marketplace\` | dedicated embed page | Visual + extension marketplace (publish / clone) |
| \`er-diagram\` | dedicated embed page | Entity-relationship diagram of the semantic model |

**Admin panels** — \`user-management\`, \`governance\`, \`identity\`, \`webhooks\`, \`usage\`, \`feature-flags\` — embed as authenticated admin surfaces (see *Embeddable Admin Panels & Snippets*).

The drop-in \`@inzara/embed-react\` / \`@inzara/embed-js\` SDKs mount all standalone content surfaces (\`insights\`, \`kpi-monitor\`, \`explore\`, \`activity\`, \`investigate\`, \`marketplace\`, \`er-diagram\`) and the id-based view surfaces (\`dashboard\`, \`report\`) as first-class resources; the admin panels embed with a delegated admin-session token.

### Security (safe to publish these docs)
- **Never ship the management API key to the browser or mobile app.** It mints sessions and must stay on your server; only the short-lived, scoped **embed session token** ever reaches the client.
- Embed session tokens are **signed, short-lived (default ~60 min), and scoped** to one tenant + workspace + user (\`sub\`) + resource — mint one per viewer.
- **Admin panels** (\`user-management\`, \`governance\`, \`identity\`, \`webhooks\`, \`usage\`, \`feature-flags\`) require an **elevated delegated admin-session token**, not a standard content session.
- Lock embedding to your **allowed parent origins** so a leaked token can't be mounted elsewhere. Data access stays enforced server-side by RBAC, row-level security, and column masking — the docs describe the surface, not a way around these controls.

### Getting your first token
1. Open **Platform Admin → API Keys** (\`/platform-admin\`), mint a Bearer key with the \`sessions.mint\` scope.
2. Server-side, call \`POST /api/mgmt/v1/embed/sessions\` with the target \`workspace_id\`, \`sub\` (viewer identity), \`resource\` (dashboard / report / kpi_monitor / insights / …), \`capabilities\` (\`read\` / \`read,write\` / \`read,write,admin\`), and \`ttl_seconds\` (≤ 3600).
3. Hand the returned JWT to the browser — never send the mgmt key to the client.

### React snippet
\`\`\`jsx
import { InzaraEmbed, RESOURCES } from '@inzara/embed-react';

<InzaraEmbed
  host="https://your-inzara-host"
  token={sessionJwt}
  resource={RESOURCES.DASHBOARD}
  id="dash_abc123"
  onDrill={({ target }) => console.log('drill:', target)}
  onFilter={({ key, value }) => console.log('filter:', key, value)}
  onTokenRefresh={(t) => console.log('token silently rotated', t)}
/>
\`\`\`

### Vanilla JS snippet
\`\`\`js
import { mount, RESOURCES } from '@inzara/embed-js';

const handle = mount(document.getElementById('inzara'), {
  host: 'https://your-inzara-host',
  token: sessionJwt,
  resource: RESOURCES.KPI_MONITOR,
  onEvent: (e) => console.log(e),
});
// Later: handle.applyFilter('status', 'alert'); handle.destroy();
\`\`\`

### Developer portal
Full endpoint reference — every path in the mgmt API v1 with copy-paste curl — lives at **[/developers](/developers)**. Paste your key once at the top and every example auto-includes it.

### Machine-readable OpenAPI spec
The frozen v1 spec is served at **[/api/mgmt/v1/openapi.json](/api/mgmt/v1/openapi.json)** — feed it to \`openapi-typescript\`, \`openapi-python-client\`, or \`oapi-codegen\` to generate a native SDK for any language.

### PostMessage event bus
| Event | Direction | Payload |
|-------|-----------|---------|
| \`viewer.identified\` | embed → host | \`{sub, roles, capabilities}\` |
| \`insight.opened\` / \`kpi.opened\` | embed → host | \`{id, kind}\` |
| \`drill.request\` | embed → host | \`{from, target}\` |
| \`drill.response\` | host → embed | \`{target, label, ttl_ms?}\` |
| \`filter.applied\` / \`filter.cleared\` | embed → host | \`{key, value?}\` |
| \`filter.set\` / \`filter.clear\` | host → embed | \`{key, value}\` / \`{key?}\` |
| \`token.refreshed\` | embed → host | \`{jti, previous_jti, expires_at}\` |
| \`error\` | embed → host | \`{code, message}\` |

### Security guarantees
- **JWT never in the iframe URL.** The SDK uses a postMessage bootstrap so the token stays out of \`document.referrer\`, browser history, and access logs.
- **Origin-locked** by the sandbox attributes; no third-party page can render your embed with a stolen token.
- **Silent auto-refresh** — the SDK re-mints via \`POST /api/embed/v2/refresh\` before expiry, preserving user scroll/state; no visual reload.
- **Budget guard** — per-user + per-workspace AI/investigation daily caps enforced on every LLM call. Breaches emit \`error\` with code \`budget_exhausted\`.
- **RLS-via-attributes** — send viewer attributes at mint time; policies filter per end-user.
`
  },
  {
    id: 'tenant-lifecycle', title: 'Tenant Management (Super Admin)', icon: Building2,
    content: `
## Tenant Management (Super Admin)

Manage every tenant across the platform — cleanup churned customers, audit expired trials, disable abuse.

### States
| Status | Meaning |
|--------|---------|
| **Active** | Normal paying tenant |
| **Trial** | In a 14-day trial window |
| **Pending Approval** | Self-serve signup awaiting SA review |
| **Suspended** | Login blocked; data preserved; reversible |
| **Pending Deletion** | Scheduled for hard-delete in 30 days; reversible until purge |

### Actions per tenant
| Action | Description |
|--------|-------------|
| **Disable** | Blocks login, preserves data. Reversible anytime |
| **Soft-delete** | Schedules purge in 30 days. Reversible until the purge fires |
| **Hard-delete** | Permanent. Requires exact tenant-name confirmation |
| **Restore** | Reinstates a suspended or pending-deletion tenant |

### Auto-cleanup cron
A background job (default: runs every 6h) auto-suspends tenants whose trial ended more than \`POST_TRIAL_GRACE_DAYS\` (default 14) ago, and hard-deletes tenants in pending-deletion past their 30-day grace. Click **Run cleanup now** on this page to force a run.

### Bulk actions
Select multiple tenants and disable / soft-delete in one click. Protected tenants (the \`system\` tenant) are never selectable.
`
  },
  {
    id: 'signup-requests', title: 'Signup Requests (approval queue)', icon: UserCheck,
    content: `
## Signup Requests

The approval queue for prospects who chose **Request access** (no invite code).

### Stats cards
| Card | What it shows |
|------|---------------|
| **Pending** | Awaiting your review |
| **Approved** | Flipped to active tenants |
| **Declined** | Rejected with an internal reason |
| **Median time-to-approve** | 50th-percentile across all approved requests |
| **Slack vs Web** | Split of approvals made from Slack buttons vs the web console |

### Per-request actions
- **Approve & start trial** — flips user to active + tenant to 14-day trial, sends welcome email
- **Decline** — flips to declined with an internal reason + sends polite rejection email

### From Slack
If your tenant has a Slack Signing Secret configured, approvals also happen right inside Slack via Block Kit buttons. The **Slack vs Web** stat card tracks which channel each approval came from.
`
  },
  {
    id: 'signup-invites', title: 'Signup Invites', icon: Ticket,
    content: `
## Signup Invites

Mint short-lived invite codes that let someone self-register with instant 14-day trial (no approval queue).

### When to use
- Invite-only waitlist clearing
- Sales demos — "click this link, you're in"
- Beta launches

### Fields
| Field | Purpose |
|-------|---------|
| **Label** | Internal name so you remember why it was minted |
| **Max uses** | 1 for one-off links, N for shared team signups |
| **Expires in** | Days before the code stops working |
| **Email lock** | Optional — only the exact email can redeem |

### Sharing
After creating, copy the full \`/register?invite=<token>\` URL and send it. The prospect signs up → their tenant goes straight to trial status.

### Revoking
Revoke any still-active code to kill the link.
`
  },
  {
    id: 'admin-console', title: 'Admin Console', icon: Server,
    content: `
## Admin Console

Top-level health + config dashboard.

### Cards
| Card | Shows |
|------|-------|
| **System Health** | DB, scheduler, WebSocket, AI service status |
| **Audit Logs** | Tail of recent actions across the tenant |
| **API Keys** | Generate tokens for programmatic access |
| **RLS Policies** | Shortcut to the RLS editor |
| **Branding** | Override logo / colours for this tenant |
| **AI Usage** | Calls, tokens, estimated cost — with breakdown by feature |
| **Explore Analytics** | Most-asked questions, most-used models |

Super admins see additional cards for cross-tenant insights.
`
  },
  {
    id: 'deploy-status', title: 'Deploy Status', icon: Server,
    content: `
## Deploy Status (Super Admin)

Live platform health snapshot. Aggregates:

| Check | What it verifies |
|-------|------------------|
| **Backend uptime** | How long the FastAPI process has been running |
| **Mongo ping** | RTT + connection status |
| **Config presence** | Required env vars, keys, SendGrid config |
| **Test email** | Fires a real SendGrid test from this page |

Use this as the first screen to check when a deployment rolls out or when something feels off across the platform.
`
  },
  {
    id: 'all-schedules', title: 'All Schedules (cross-tenant)', icon: CalendarClock,
    content: `
## All Schedules (Super Admin)

A super-admin cross-tenant view of every schedule, burst, and alert — useful when hunting for a runaway recurring email.

### Filters
- **Kind** — schedule / burst / alert
- **Search** — name, recipient, or tenant substring
- **Enabled only** — hide disabled rows

### Actions
- **Toggle** — enable/disable any schedule without jumping tenants
- **Delete** — remove a schedule entirely (audit-logged)

Handy when a customer escalates "I'm getting 400 emails a day" — one-click toggle and the noise stops.
`
  },
  {
    id: 'background-jobs', title: 'Background Jobs', icon: Briefcase,
    content: `
## Background Jobs

Queue status + history for every async job: report exports, bulk imports, long-running AI generations.

### Per-job
- Queue, status (pending / running / completed / failed)
- Payload summary
- Retry button (for failed jobs)
- Full error stack trace in the detail modal

### Troubleshooting
If jobs are stuck in "pending" for a long time, check Deploy Status — the scheduler process may not be running.
`
  },
  {
    id: 'trial-leads', title: 'Trial Leads', icon: UserCheck,
    content: `
## Trial Leads (Share Leads)

When a prospect opens a public-share link to one of your dashboards, their session metadata is logged here as a lead.

### What you see
- Dashboard opened
- First-touch timestamp
- Device / browser
- Referrer
- Time-on-page

### Actions
- **Email this lead** — opens a SendGrid-templated mailto with the dashboard URL
- **Add to CRM** (if you've wired one up via the Embed / Webhook SDK)
- **Dismiss** — hide uninteresting leads

Useful for sales teams sending dashboards cold.
`
  },
  {
    id: 'inbound-emails', title: 'Email-to-Insight', icon: Inbox,
    content: `
## Email-to-Insight

Turn INZARA into an ambient assistant: your team emails a plain-English question to your workspace's inbound address, and INZARA replies with the answer — no login required.

### Your inbound email address
The address customers write to is **whatever address you set up to receive mail** (in Resend or SendGrid) and route to INZARA's webhook. It is **not** a fixed \`@inzara.com\` address — you use your own domain (e.g. \`ask@yourcompany.com\`).

Set and view it under **Admin → Email-to-Insight**, in the **"Where customers send emails"** card at the top of the page. Copy it from there and share it with your team.

### Setup (one-time, admin)
1. In **Resend** (resend.com/domains): verify your domain and enable **Receiving**, then add a webhook for the \`email.received\` event pointing to the webhook URL shown on the Email-to-Insight page. *(Legacy SendGrid Inbound Parse is also supported — point MX records at it and POST to the SendGrid webhook URL.)*
2. On **Admin → Email-to-Insight**, paste your Resend signing secret (\`whsec_…\`) so the webhook is signature-verified.
3. In the **"Where customers send emails"** card, enter the address you configured to receive mail (e.g. \`ask@yourcompany.com\`) and Save. We suggest \`ask@<your sending domain>\` automatically.
4. Click **"Send myself a test"** to run the whole loop end-to-end — INZARA emails a sample answer to your own address so you can confirm it works before sharing.

### Sender allowlist (optional)
Restrict who can ask by listing approved sender domains (e.g. \`yourcompany.com\`) in the **Sender allowlist** card. When set, questions from any other domain are rejected (logged as **blocked domain**) so strangers can't probe your data. Leave it blank to allow any registered user.

### Reply branding (optional)
Customise the footer of every AI email reply in the **Reply branding** card — set your own sign-off line and an "open the app" link so replies match your brand.

### What happens
- A user emails a question (plain English) to your inbound address.
- INZARA matches the sender to their account, runs the AI assistant against your tenant's semantic model, and **replies to the email** with the answer within ~60s.
- Every inbound question + status (received / answered / AI failed / unknown sender) is logged in this page's inbox for audit.

### Troubleshooting
- **Customers don't know where to write**: set the inbound address in the "Where customers send emails" card.
- **No reply**: confirm outbound email is configured (status badge on the page) and that the sender is a registered user — unknown senders get a polite bounce, not an answer.
- **Webhook rejected (401)**: the signing secret on the page must match the one in Resend/SendGrid.
`
  },
  {
    id: 'system-logs', title: 'System Logs', icon: Server,
    content: `
## System Logs (Super Admin)

Tails the backend and scheduler logs with live streaming.

### Filters
- Level (info / warn / error)
- Module (scheduler, email_service, query_engine, …)
- Tenant

### Search
Regex-capable. Example: \`ERROR.*CSRF\` finds every CSRF failure.

### Download
Download the last N minutes of logs as plain text — useful for attaching to a support ticket.
`
  },
  {
    id: 'mgmt-api', title: 'Management API, Keys & Sandbox', icon: CodeIcon,
    content: `
## Partner Management API

Provision and administer tenants headlessly with scoped management API keys under \`/api/mgmt/v1\`.

### Creating keys
Developer Portal → API Keys. Each key has:
- **Scopes** — least-privilege grants (e.g. \`billing:read\`, \`webhooks:write\`, \`api_keys:read\`).
- **Environment** — \`live\` or \`test\` (sandbox).
- **Rate limit** — requests/minute (defaults: 120 live, 30 sandbox).

Secret keys (\`sk_live_…\` / \`sk_test_…\`) are shown once at creation — copy and store them safely.

### Sandbox
Use a \`test\` key to try calls without touching production limits. Responses carry an \`Inzara-Environment\` header. Over-limit calls return **429** with rate-limit headers.

### Rate-Limit Insights
Developer Portal → Rate-Limit Insights shows a 24-hour per-key usage-vs-limit chart so you can see how close each key runs to its ceiling.

### Capability areas
The v1 management API now spans the full platform. In the **[Developer Portal](/developers)** the reference is grouped by area — paste your key once and every endpoint gets a ready-to-run curl:
- **Organizations & Workspaces** — provision orgs, workspaces, branding, custom domains, layout presets, starter questions.
- **Workspace Users & Budgets** — invite/scope users, set per-user AI budgets.
- **Roles & Permissions** — read the permission catalog, create custom roles.
- **Policies (RLS & Masking)** — row-level security and column-masking policies with per-policy toggles.
- **Identity & SCIM** — SCIM tokens, JIT group→role mappings, IdP config.
- **Feature Flags** — catalog + per-workspace overrides.
- **Cost Alerts** — monthly spend tiers, history, Slack test.
- **Webhooks** — endpoints, event catalog, secret rotation, delivery log + replay.
- **Usage & Metering** — current-month totals, query events, digest preview, rate-limit keys.
- **Embed Sessions** — mint / verify / revoke session JWTs, list surfaces.
- **Admin Sessions, Audit & Certifications, Governance Cleanup, Platform** — session control, audit export, certification, cleanup queue, ingress/verified-domains.

The complete, machine-readable contract is always at **[/api/mgmt/v1/openapi.json](/api/mgmt/v1/openapi.json)** (also frozen in \`sdk/openapi/mgmt_openapi_v1.json\`).
`
  },
  {
    id: 'delegated-admin', title: 'Delegated Admin Access', icon: ShieldCheck,
    content: `
## Delegated Admin Sessions

Mint short-lived, bounded admin tokens for partners to administer a tenant, org or workspace without full platform access.

### Scopes
\`users:admin\`, \`roles:admin\`, \`governance:admin\`, \`audit:read\`, \`datasources:admin\`, \`billing:read\`, \`identity:admin\`, \`billing:write\`.

### act_as
Actions taken with a delegated token are attributed in the audit trail to the acting partner identity, preserving accountability.

### Organizations
- **Direct** orgs manage a single tenant.
- **Reseller** orgs provision and manage many downstream tenants; the backend always generates tenant IDs.
`
  },
  {
    id: 'identity-bridge', title: 'Identity Bridge — SCIM & JIT', icon: ShieldCheck,
    content: `
## Identity Bridge

Federate identity from your IdP (Okta, Entra, OneLogin, …) using generic SCIM 2.0 or just-in-time provisioning.

### Modes
- **internal** — INZARA owns users and roles.
- **federated** — your IdP drives provisioning via SCIM/JIT.

### SCIM
INZARA issues a per-tenant SCIM bearer token (revealed once). Endpoints live under \`/api/scim/v2/*\`.
- User create / update / deactivate.
- Deprovisioning **soft-disables** users (data kept, login blocked).
- Unmapped IdP groups fall back to a safe default role (\`viewer\`).

### Group mapping
Map IdP groups to INZARA roles in the embedded Identity panel.
`
  },
  {
    id: 'signed-webhooks', title: 'Signed Webhooks & Cost Alerts', icon: Bell,
    content: `
## Signed Outbound Webhooks

Get notified of identity, governance and audit events with signed, at-least-once delivery.

### Reliability
- Retry with backoff, a delivery log, and manual replay.
- Signing secret shown once when the endpoint is created or rotated.
- Subscribe to specific events via the tap-to-add **event picker** (empty = all events).

### Cost Alerts
Set monthly spend thresholds per tenant. When month-to-date query cost crosses a tier you get a \`tenant.cost_threshold_exceeded\` webhook (once per tier per month).
- Multiple tiers (e.g. warning + critical), each firing its own alert.
- Optional **Slack** delivery to an Incoming Webhook URL, with a Test button.
- A **Recent alerts** timeline records every crossing.
`
  },
  {
    id: 'embed-admin-panels', title: 'Embeddable Admin Panels & Snippets', icon: Layers,
    content: `
## Embeddable Admin Panels

Drop INZARA admin surfaces straight into your own product, authenticated by a delegated admin token — no INZARA chrome or login.

### Available panels
User Management, Governance, Identity, Webhooks, Usage & Billing, Feature Flags.

### Content embeds
Insights, KPI Monitor, dashboards, reports and more via the \`@inzara/embed-react\` SDK or an iframe.

### Snippet Generator
Developer Portal → Embed Snippet Generator builds copy-paste **iframe** and **React SDK** snippets for any surface. Pick the surface, set host/height, and copy — tokens are placeholders you swap at runtime (never paste a real key in the browser).
`
  },
  {
    id: 'workspace-flags', title: 'Workspace Feature Flags', icon: SettingsIcon,
    content: `
## Workspace Feature Flags

Turn capabilities on or off per workspace without a deploy.

### Catalog
Flags are grouped by area — AI, Export, Engagement, Collaboration, Distribution.

### Resolution
Effective value = workspace override (if set) → otherwise the catalog default. The embedded **Feature Flags** admin panel shows the source (default vs workspace override) for each flag and lets you toggle overrides per workspace.
`
  },

  {
    id: 'sentinels', title: 'Governed Sentinels', icon: ShieldCheck,
    content: `
## Governed Sentinels

Sentinels are governed, versioned monitors that watch a certified metric and raise a managed **incident** the moment a condition is both statistically real and materially significant. Detection runs entirely on your own governed data — deterministic and local, with no customer values ever sent to an external AI. They coexist with the older Forecast Sentinels.

### When to use
Use a Sentinel when you want continuous, auditable monitoring of a metric (loss ratio, incurred loss, reserve drift, claim frequency…) rather than a one-off check.

### Create a Sentinel
1. Open **AI Platform → Sentinels** and click **New Sentinel** (or **New from pack** to start from a saved template).
2. Name it, pick the **model** and the **metric**, and optionally a date field and grain.
3. Add one or more **detection methods** (below) and set their thresholds.
4. Set the **materiality** bar, **persistence** (how many periods must breach), **cooldown**, **severity** and **recipients**.
5. **Save as draft** to keep tuning, or **Save & activate** to start monitoring.

### Detection methods
| Method | Fires when… |
| --- | --- |
| Static threshold | the value crosses a fixed line |
| Dynamic threshold | the value crosses a rolling / statistical band |
| Rate of change | the period-over-period change is too large |
| Sustained trend | the metric moves the same way for N periods |
| Seasonal anomaly | the value is unusual vs its seasonal norm |
| Forecast threshold | the forecast crosses a line |
| Forecast variance | actuals diverge from the forecast |
| Data-quality event | freshness or completeness degrades |

### Evidence & materiality gate
A breach only becomes an incident when it clears BOTH the statistical test and the materiality bar (for example a 5% change sustained for 3 periods). This keeps the Incidents inbox signal, not noise.

### Evaluate, replay & lifecycle
- **Evaluate now** runs the Sentinel immediately against the latest data.
- **Replay history** re-runs it over past periods so you can see when it would have fired.
- Incidents move through **open → acknowledged → assigned → resolved → recovery-verified**, and every Sentinel edit is versioned and audited.
- Material actions never auto-execute — a Sentinel only notifies and recommends.
`
  },

  {
    id: 'sentinel-packs', title: 'Sentinel Pack Library', icon: Layers,
    content: `
## Sentinel Pack Library

A **pack** is a reusable template of a tuned Sentinel — its detection methods, thresholds, materiality, persistence, cooldown, severity and recipients — without the metric-specific bits. Stamp a pack onto any metric to create a consistent Sentinel in one step.

### Save a pack
On a Sentinel's detail page click **Save as pack** and give it a name. The pack captures the current configuration.

### Manage packs
Open **AI Platform → Sentinel Packs** to rename, describe, **categorise** (general / risk / compliance / operations / financial), **retire** (hide from pickers without deleting) or delete a pack. Each row shows how many times the pack has been used.

### Stamp a pack onto metrics
- **New from pack** (on the Sentinels list) opens the builder pre-filled from the pack — just pick a model and metric, then Save & activate.
- **Bulk stamp** (in the library) applies one pack across several metrics of a model at once, creating one draft Sentinel per metric. Review and activate the drafts afterwards.
`
  },

  {
    id: 'incidents', title: 'Incidents & Recovery Digest', icon: Inbox,
    content: `
## Incidents & Recovery Digest

The **Incidents** inbox lists every managed incident a Sentinel has raised, with its evidence, severity and reproducibility. It's where the team triages and closes out Sentinel findings.

### Work an incident
1. Open **AI Platform → Incidents** and filter by status or severity.
2. Select an incident to read its **evidence** (what changed, the detection method, the materiality).
3. Move it through the lifecycle: **Acknowledge → Assign → Resolve → Verify recovery**. Every transition records who and when.
4. Use **Investigate why** to hand the incident to root-cause analysis.

### Recovery Digest
The Recovery Digest panel (top of the Incidents page) gives a weekly recap of incident response: which Sentinels fired, who resolved them, mean time to resolve, and how quickly recovery was verified.
- Toggle **Off / Weekly** to control the automatic cadence.
- **Recipients** — leave empty to send to all stewards, admins and managers, or pick specific people.
- **Test to me** sends you a one-off preview copy without changing the schedule.
- **Send now** delivers the digest immediately.
`
  },

  {
    id: 'drift-alerts', title: 'Drift Alert Center', icon: Bell,
    content: `
## Drift Alert Center

When a predictive model tips into **critical drift**, an alert lands here — a dedicated inbox for model owners and stewards so drift never slips by unnoticed.

### Use it
1. Open **AI Platform → Drift Alert Center**. Unread alerts are highlighted with a count.
2. Each alert shows the **model**, when it happened, and the drift severity.
3. Click **Scorecard** to jump straight to that model's registry entry and recertify it.
4. **Mark read** (per alert) or **Mark all read** to clear the queue.

Alerts are delivered to your in-app inbox the moment a model transitions into critical drift; you are not re-alerted while it stays critical.
`
  },

  {
    id: 'scenario-simulator', title: 'Scenario Simulator & Compare', icon: TrendingUp,
    content: `
## Scenario Simulator & Compare

Model a "what-if" — a rate change, loss-trend shift, frequency change or reserve strengthening — and see the projected impact on your risk signals **before** you recommend anything. It's deterministic, computed on your own governed data, and nothing is executed.

### Run a simulation
1. Open **AI Platform → Scenario Simulator**, pick a model and optionally a line-of-business scope.
2. Set one or more levers to a non-zero value. If every lever is left at zero you'll see a "no change from baseline" hint.
3. Click **Run simulation** to see which signals resolve or worsen.
4. **Save** a promising scenario so it can be reused and attached to an approval.

### Compare scenarios
- The **Saved scenarios** section lets you tick **two** scenarios to line them up side by side — levers, projected impact and per-signal before → after.
- The **All models / This model** toggle lets you compare scenarios even across different models; each card shows its model.
- A **Resolves more** badge marks whichever scenario clears more firing signals.

### Attach to an approval
On the winning compared scenario click **Attach to approval** — it opens the Recommendation Rules page with the model pre-selected and the scenario pre-attached as rationale when you submit a material recommendation for approval.
`
  },

  {
    id: 'risk-signals', title: 'Risk Signals & Threshold Presets', icon: Gauge,
    content: `
## Risk Signals & Threshold Presets

The **Risk Signals** page surfaces deterministic early-warning indicators — loss-ratio, claim-frequency and reserve-drift signals computed from your own governed data — with a watchlist and per-model scorecards. No external AI.

### Run signals
Pick a model and click **Run signals** to compute the latest indicators; runs trend over time and refresh daily.

### Threshold presets
Presets are named sets of warn / alert thresholds you can reuse across models.
1. Open **Thresholds** and tune the bars — values like 0.55 mean 55%.
2. **Save as preset**, then star a preset to make it the **organization default**; untuned models inherit it automatically.
3. The **audit line** beside the default star shows who set the org default and when, so governance is always clear.

Per-line-of-business overrides let you hold different lines to different bars.
`
  },

  {
    id: 'recommendation-rules', title: 'Recommendation Rules & Approvals', icon: Lightbulb,
    content: `
## Recommendation Rules & Approvals

Recommendation Rules turn a risk signal into a governed, **advisory** recommendation — a suggested next step for a human. Nothing is ever auto-executed: a rule only proposes, and material recommendations must clear a named human approval before anyone acts.

### Build a rule
1. Open **AI Platform → Recommendation Rules**.
2. Give the rule a name and pick the signal **condition** that should trigger it (for example loss-ratio alert on a line of business).
3. Choose the **advisory action** to recommend and write the guidance the reviewer will see.
4. Mark whether the outcome is **material** — material recommendations are routed for approval; non-material ones are advisory only.
5. **Save** to activate, or **Pause** a rule to stop it firing without deleting it.

### How recommendations are produced
When a matching signal fires, the rule emits a recommendation with its evidence. Generation is deterministic and computed on your own governed data — no customer values are sent to an external AI.

### Human approval (four-eyes) & audit
- A **material** recommendation goes to the approvals queue. Open **AI Platform → Recommendation Approvals** to review it.
- A **named approver** (someone other than the author — four-eyes) must **Approve** or **Reject** it; the author cannot approve their own.
- Every decision and action records who, when and why, forming an immutable audit trail.

### Attach a scenario as rationale
From the **Scenario Simulator** you can attach a saved (or compared) scenario to a material recommendation. The projected impact travels with the recommendation so the approver sees the "what-if" evidence behind the ask.
`
  },

  {
    id: 'ai-coach', title: 'AI Coach & Guided Walkthroughs', icon: Sparkles,
    content: `
## AI Coach & Guided Walkthroughs

The AI Coach is the in-app helper that points you at exactly the right menu or button and walks you through a task step by step. It never clicks for you on risky actions — it highlights the control, explains the step, and verifies you did it before moving on.

### Opening the Coach
- Click the **Coach** launcher (bottom-right). Ask a question in plain language, or pick a suggested walkthrough for the screen you're on.
- The Coach highlights the target control with a coloured ring and a **bouncing arrow** so you always know where to look.

### How a walkthrough works
1. The Coach shows one step at a time with a short instruction.
2. You do the highlighted action in the app.
3. The Coach **verifies each step server-side** and advances automatically — you never mark a step "done" yourself.
4. A running progress indicator shows *step X of Y*.

### Auto-advance (the Coach follows you)
When a step is waiting on a specific control, simply **clicking the highlighted element** advances the walkthrough — no separate "Next" button. For builder flows the Coach also listens for the real events (model chosen, measure added, chart type picked) and moves on as you complete them.

### Reversible actions & undo
For the few steps the Coach can perform for you (all reversible, UI-only), it asks for an explicit **Confirm & apply** first, and offers a one-tap **Undo** afterwards. No data is ever changed on your behalf.

### Customising the highlight (admins)
Admins can set the highlight **arrow colour** and turn the bounce on/off under **Administration → Coach Autonomy → Highlight arrow**.

> The Coach only ever *guides*. Completion, permissions and approvals are always decided by the platform, never asserted by the assistant.
`
  },
  {
    id: 'autonomous-coach', title: 'Autonomous Coach (plan runtime)', icon: Brain,
    content: `
## Autonomous Coach

The Autonomous Coach turns a goal you type ("build a report of written premium by state as a bar chart") into a **validated, step-by-step plan** you can run with live, server-verified completion. It is **off by default** and enabled per workspace by an admin.

### Turning it on
Open **Administration → Coach Autonomy** and toggle **Autonomous plan runtime** on. Requires an admin/owner role.

### Running a goal
1. In the Coach panel, switch to the autonomous mode and type what you want to accomplish.
2. The server **resolves → plans → validates** the request and shows you the plan (with a risk badge) before anything runs.
3. Start the plan and follow the highlighted steps; each step completes only when the platform confirms the required event happened.
4. If a request needs more detail (e.g. a missing measure or dimension) the Coach asks for it rather than guessing.

### Safety model
- The plan is **immutable for the life of the run** — you always step through the plan you started.
- Completion is evaluated **server-side per event**; the client/AI can never assert a step or goal is complete.
- Nothing data-mutating is executed autonomously. Guidance-only steps that act on your behalf are reversible, confirmed, and undoable.
- If a run keeps diverging from the plan, the Coach offers a **guided walkthrough** instead.

### For admins — the control center
**Administration → Coach Autonomy** also gives you:
- A **capability graph** of every coachable task and the permissions/screen targets it needs.
- A **Plan inspector** to preview the exact steps for any request (read-only, no runtime needed).
- **Coverage**, **Plan run health**, **trends**, and a **redacted list of recent runs** so you can see completion vs deviation over time.

> Completion is a structural guarantee: *false completions are 0 by construction* because every step is gated on a server-evaluated predicate.
`
  },
  {
    id: 'capability-studio', title: 'Capability Studio & Record-to-Discover', icon: Wand2,
    content: `
## Capability Studio

Capability Studio lets admins add new coachable tasks **without writing code** — the Coach can then guide users to any menu or button you compose a capability for. You compose only from existing, allowlisted building blocks, so the deterministic safety of the planner is fully preserved.

Open it under **Administration → Coach Autonomy → Capability Studio** (admin role required).

### Compose a capability
1. Click **New capability** and give it a name and a short, action-oriented description.
2. **Screen target** — search the grouped catalog (menus and buttons across every area) and pick the control the Coach should highlight.
3. **Completion event** — choose the trusted event that proves the step is done (e.g. \`control_ping\` when the user clicks the highlighted control, or a builder event).
4. **Permissions / prerequisites / risk** — pick real RBAC permissions and any prerequisite steps.
5. **Trigger phrases** — how users ask for this in natural language.

You can never invent a target, event or permission — only choose from what really exists.

### Test before you publish
- **Test a phrase** shows which capability a sample question would trigger. Built-in tasks always win a collision, so the tester suggests a more distinctive phrase when needed.
- **Duplicate** an existing capability as a starting point; **Publish** makes it a live, governed capability; **Retire** takes it out of use.

### Record-to-Discover (the catalog fills itself)
While recording a demonstration, the Coach also captures controls you clicked that carry a stable id but **aren't in the catalog yet**. These appear under **Discovered from recordings** — click **Add** to promote one into the catalog with a friendly name. Promoted controls resolve on-screen immediately, so you can build a capability that points at them with no code change.

### Coverage
The **Coverage** card shows, per app area, how many controls are catalogued and how many already have a coachable task — plus the controls seen in recordings that still need promoting. Use it to spot gaps in what the Coach can reach.
`
  },

  ...HELP_SECTIONS_EXTRA,
];


const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Help center personalization (local only) — recent searches + view counts.
const RECENT_KEY = 'inzara_help_recent';
const VIEWS_KEY = 'inzara_help_views';
const _readLS = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k)); return v == null ? d : v; } catch { return d; } };
const _writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };
// Curated fallback shown until real view data accrues.
const POPULAR_FALLBACK = ['quickstart', 'data-sources', 'semantic-models', 'access-control', 'explore-ai', 'alerts'];

export default function HelpPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const APP_NAME = useBrandName();
  const isAdmin = ['admin', 'tenant_admin', 'super_admin'].includes(user?.role);
  // Articles now live in the DB. Start from the in-code SECTIONS (fallback +
  // seed source), then load the DB copy on mount.
  const [sections, setSections] = useState(SECTIONS);

  const loadArticles = React.useCallback(async () => {
    try {
      let r = await api.getHelpArticles();
      let arts = r?.articles || [];
      // KB empty — an admin seeds it once from the built-in articles.
      if (arts.length === 0 && isAdmin) {
        const seed = SECTIONS.map((s, i) => ({ id: s.id, title: s.title, icon: iconName(s.icon), content: s.content, order: i }));
        await api.importHelpArticles(seed);
        r = await api.getHelpArticles();
        arts = r?.articles || [];
      } else if (arts.length > 0 && isAdmin) {
        // Keep the KB in sync with any newly-shipped built-in articles.
        const have = new Set(arts.map(a => a.article_id));
        const missing = SECTIONS.filter(s => !have.has(s.id));
        if (missing.length) {
          for (const s of missing) {
            try {
              await api.createHelpArticle({ article_id: s.id, title: s.title, icon: iconName(s.icon), content: s.content, order: 900 });
            } catch { /* already exists — ignore */ }
          }
          r = await api.getHelpArticles();
          arts = r?.articles || [];
        }
      }
      if (arts.length > 0) setSections(arts.map(a => ({ id: a.article_id, title: a.title, icon: iconFor(a.icon), content: a.content })));
    } catch { /* fall back to in-code SECTIONS */ }
  }, [isAdmin]);
  useEffect(() => { loadArticles(); }, [loadArticles]);

  // Single source of truth: the active section lives in the URL (?s=).
  // Deriving it — instead of mirroring it into separate state and syncing
  // both ways — eliminates the state<->URL loop that made section clicks
  // flicker between pages and never settle.
  const activeSection = useMemo(() => {
    const s = searchParams.get('s');
    if (sections.some(x => x.id === s)) return s;
    if (SECTIONS.some(x => x.id === s)) return s;
    return (sections[0] && sections[0].id) || SECTIONS[0].id;
  }, [searchParams, sections]);
  const setActiveSection = React.useCallback((id) => {
    setSearchParams({ s: id });
  }, [setSearchParams]);
  // Help sidebar mirrors the main system navigation (grouped + nav-ordered).
  const helpGroups = useMemo(() => buildHelpGroups(sections), [sections]);
  const [search, setSearch] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const pendingAnchorRef = useRef(
    (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('h') : null) || null
  );

  // Scroll the content area to the top on section change — unless a quick-jump
  // anchor is pending (the next effect scrolls to the heading instead).
  useEffect(() => {
    if (pendingAnchorRef.current) return;
    const el = document.querySelector('[data-testid="help-content"]');
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSection]);

  // After a quick-jump into a section, scroll to the target sub-heading.
  useEffect(() => {
    if (!pendingAnchorRef.current) return;
    const anchor = pendingAnchorRef.current;
    const t = setTimeout(() => {
      const el = document.getElementById(`help-h-${anchor}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pendingAnchorRef.current = null;
    }, 80);
    return () => clearTimeout(t);
  }, [activeSection]);

  // Flat index of every section title + sub-heading for quick-jump search.
  const suggestIndex = useMemo(() => {
    const out = [];
    for (const s of sections) {
      out.push({ sectionId: s.id, sectionTitle: s.title, icon: s.icon, label: s.title,
        anchor: null, type: 'section', body: s.content.toLowerCase() });
      for (const raw of s.content.split('\n')) {
        const line = raw.trim();
        let heading = null;
        if (line.startsWith('### ')) heading = line.slice(4);
        else if (line.startsWith('## ')) heading = line.slice(3);
        if (heading) {
          out.push({ sectionId: s.id, sectionTitle: s.title, icon: s.icon,
            label: heading.replace(/\*\*/g, ''), anchor: slugify(heading), type: 'heading' });
        }
      }
    }
    return out;
  }, [sections]);

  const q = search.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (q.length < 2) return [];
    return suggestIndex
      .filter(e => e.label.toLowerCase().includes(q) || e.sectionTitle.toLowerCase().includes(q) || (e.body && e.body.includes(q)))
      .sort((a, b) => {
        const aLabel = a.label.toLowerCase().includes(q) ? 0 : 1;
        const bLabel = b.label.toLowerCase().includes(q) ? 0 : 1;
        if (aLabel !== bLabel) return aLabel - bLabel;
        const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        if (a.type !== b.type) return a.type === 'section' ? -1 : 1;
        return a.label.length - b.label.length;
      })
      .slice(0, 8);
  }, [q, suggestIndex]);

  useEffect(() => { setActiveIdx(0); }, [q]);

  const jumpTo = (e) => {
    if (!e) return;
    if (search.trim()) addRecent(search.trim());
    pendingAnchorRef.current = e.anchor;
    trackView(e.sectionId);
    setActiveSection(e.sectionId);
    setShowSuggest(false);
    setSearch('');
  };

  const onSearchKeyDown = (ev) => {
    if (!suggestions.length) return;
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (ev.key === 'Enter') { ev.preventDefault(); jumpTo(suggestions[activeIdx]); }
    else if (ev.key === 'Escape') { setShowSuggest(false); }
  };

  const activeContent = sections.find(s => s.id === activeSection);

  // Recent searches + popular topics (view-count driven, local only).
  const [recent, setRecent] = useState(() => _readLS(RECENT_KEY, []));
  const [views, setViews] = useState(() => _readLS(VIEWS_KEY, {}));

  const addRecent = (query) => {
    const q2 = query.trim();
    if (q2.length < 2) return;
    setRecent(prev => {
      const next = [q2, ...prev.filter(r => r.toLowerCase() !== q2.toLowerCase())].slice(0, 6);
      _writeLS(RECENT_KEY, next);
      return next;
    });
  };

  const trackView = (sectionId) => {
    setViews(prev => {
      const next = { ...prev, [sectionId]: (prev[sectionId] || 0) + 1 };
      _writeLS(VIEWS_KEY, next);
      return next;
    });
  };

  // Help feedback ("Was this helpful?") — persisted per (user, section).
  const [helpVotes, setHelpVotes] = useState({});
  const [helpSaving, setHelpSaving] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.getMyHelpFeedback()
      .then(r => { if (!cancelled) setHelpVotes(r?.votes || {}); })
      .catch(() => { /* not signed in / optional */ });
    return () => { cancelled = true; };
  }, []);

  const sendHelpVote = async (sectionId, helpful) => {
    if (helpSaving) return;
    setHelpVotes(prev => ({ ...prev, [sectionId]: helpful }));
    setHelpSaving(true);
    try { await api.submitHelpFeedback(sectionId, helpful); }
    catch { /* keep optimistic state; best-effort */ }
    finally { setHelpSaving(false); }
  };

  const popular = useMemo(() => {
    const ranked = Object.entries(views).sort((a, b) => b[1] - a[1]).map(([id]) => id);
    const ids = [...ranked, ...POPULAR_FALLBACK].filter((id, i, arr) => arr.indexOf(id) === i);
    return ids.map(id => sections.find(s => s.id === id)).filter(Boolean).slice(0, 5);
  }, [views, sections]);

  // ─── Admin inline editing ───
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({ article_id: '', title: '', content: '', isNew: false });
  const [editSaving, setEditSaving] = useState(false);

  const openEditArticle = () => {
    if (!activeContent) return;
    setEditDraft({ article_id: activeContent.id, title: activeContent.title, content: activeContent.content, isNew: false });
    setEditOpen(true);
  };
  const openNewArticle = () => {
    setEditDraft({ article_id: '', title: '', content: '## New article\n\nWrite Markdown here…', isNew: true });
    setEditOpen(true);
  };
  const saveArticle = async () => {
    if (!editDraft.title.trim()) { toast.error('Title is required'); return; }
    setEditSaving(true);
    try {
      if (editDraft.isNew) {
        const created = await api.createHelpArticle({ article_id: editDraft.article_id || editDraft.title, title: editDraft.title, content: editDraft.content });
        await loadArticles();
        setActiveSection(created.article_id);
        toast.success('Article created');
      } else {
        await api.updateHelpArticle(editDraft.article_id, { title: editDraft.title, content: editDraft.content });
        await loadArticles();
        toast.success('Article saved');
      }
      setEditOpen(false);
    } catch (err) { toast.error(err.message); }
    finally { setEditSaving(false); }
  };
  const deleteArticle = async () => {
    if (!activeContent || !window.confirm(`Delete "${activeContent.title}"? This can't be undone.`)) return;
    try {
      await api.deleteHelpArticle(activeContent.id);
      const remaining = sections.filter(s => s.id !== activeContent.id);
      await loadArticles();
      if (remaining[0]) setActiveSection(remaining[0].id);
      toast.success('Article deleted');
    } catch (err) { toast.error(err.message); }
  };

  const openSection = (sectionId) => {
    pendingAnchorRef.current = null;
    trackView(sectionId);
    setActiveSection(sectionId);
    setShowSuggest(false);
  };

  // Simple markdown-like renderer
  const renderContent = (rawText) => {
    const text = APP_NAME === 'INZARA' ? rawText : rawText.replace(/\bINZARA\b(?!_)/g, APP_NAME);
    const lines = text.trim().split('\n');
    const elements = [];
    let inTable = false;
    let tableRows = [];
    let tableHeaders = [];

    const flushTable = () => {
      if (tableHeaders.length > 0) {
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-auto my-3">
            <table className="w-full text-xs border-collapse border border-border/30 rounded-lg">
              <thead>
                <tr className="bg-muted/30">
                  {tableHeaders.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border/30">{h.trim()}</th>)}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 1 ? 'bg-muted/10' : ''}>
                    {row.map((cell, ci) => <td key={ci} className="px-3 py-1.5 text-foreground/80 border-b border-border/20">{renderInline(cell.trim())}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      tableHeaders = [];
      tableRows = [];
      inTable = false;
    };

    const renderInline = (text) => {
      // Bold
      text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Code
      text = text.replace(/`(.*?)`/g, '<code class="px-1 py-0.5 rounded bg-muted/50 text-primary text-[10px] font-mono">$1</code>');
      return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Table row
      if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        const cells = line.split('|').filter(c => c.trim() !== '');
        if (!inTable) {
          inTable = true;
          tableHeaders = cells;
        } else if (cells.every(c => c.trim().match(/^[-:]+$/))) {
          continue; // separator row
        } else {
          tableRows.push(cells);
        }
        continue;
      }

      if (inTable) flushTable();

      if (line.trim() === '') {
        continue;
      } else if (line.trim().match(/^!\[.*\]\(.*\)$/)) {
        const m = line.trim().match(/^!\[(.*)\]\((.*)\)$/);
        elements.push(
          <figure key={i} className="my-4">
            <img src={m[2]} alt={m[1]} loading="lazy" className="rounded-lg border border-border/40 shadow-sm w-full" />
            {m[1] && <figcaption className="text-[11px] text-muted-foreground mt-1.5 text-center">{m[1]}</figcaption>}
          </figure>
        );
      } else if (line.startsWith('## ')) {
        elements.push(<h2 key={i} id={`help-h-${slugify(line.slice(3))}`} className="text-lg font-heading font-bold text-foreground mt-6 mb-3 scroll-mt-4">{line.slice(3)}</h2>);
      } else if (line.startsWith('### ')) {
        elements.push(<h3 key={i} id={`help-h-${slugify(line.slice(4))}`} className="text-sm font-heading font-semibold text-foreground mt-4 mb-2 scroll-mt-4">{line.slice(4)}</h3>);
      } else if (line.startsWith('#### ')) {
        elements.push(<h4 key={i} className="text-xs font-heading font-semibold text-primary mt-3 mb-1">{line.slice(5)}</h4>);
      } else {
        elements.push(<p key={i} className="text-sm text-foreground/80 leading-relaxed mb-2">{renderInline(line)}</p>);
      }
    }

    if (inTable) flushTable();
    return elements;
  };

  return (
    <div className="flex h-full" data-testid="help-page">
      {/* Sidebar */}
      <div className="w-64 border-r border-border/50 flex flex-col overflow-hidden flex-shrink-0">
        <div className="p-4 border-b border-border/50">
          <h1 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" /> Help &amp; Documentation
          </h1>
          <div className="relative mt-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground z-10" />
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 120)}
              onKeyDown={onSearchKeyDown}
              placeholder="Search docs…"
              className="pl-8 h-8 text-xs"
              data-testid="help-search-input"
            />
            {showSuggest && suggestions.length > 0 && (
              <div
                className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg border border-border/50 bg-popover shadow-xl overflow-hidden"
                data-testid="help-search-suggestions"
              >
                {suggestions.map((e, i) => {
                  const Icon = e.icon;
                  return (
                    <button
                      key={`${e.sectionId}-${e.anchor || 'root'}-${i}`}
                      // onMouseDown fires before the input's onBlur so the jump registers.
                      onMouseDown={(ev) => { ev.preventDefault(); jumpTo(e); }}
                      onMouseEnter={() => setActiveIdx(i)}
                      data-testid={`help-suggestion-${i}`}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors ${i === activeIdx ? 'bg-primary/10' : 'hover:bg-muted/30'}`}
                    >
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${e.type === 'section' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs text-foreground truncate">{e.label}</span>
                        {e.type === 'heading' && (
                          <span className="block text-[10px] text-muted-foreground truncate">in {e.sectionTitle}</span>
                        )}
                      </span>
                      <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
            {showSuggest && q.length < 2 && (
              <div
                className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg border border-border/50 bg-popover shadow-xl overflow-hidden py-1"
                data-testid="help-search-quicklinks"
              >
                {recent.length > 0 && (
                  <div className="px-3 py-2 border-b border-border/30">
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Recent searches</p>
                    <div className="flex flex-wrap gap-1.5">
                      {recent.map((r, i) => (
                        <button
                          key={r}
                          onMouseDown={(ev) => { ev.preventDefault(); setSearch(r); setShowSuggest(true); }}
                          data-testid={`help-recent-${i}`}
                          className="px-2 py-1 rounded-full text-[10px] bg-muted/40 text-foreground/80 hover:bg-primary/15 hover:text-primary transition-colors flex items-center gap-1"
                        >
                          <Clock className="w-2.5 h-2.5" /> {r}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="px-3 py-2">
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Popular topics</p>
                  {popular.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.id}
                        onMouseDown={(ev) => { ev.preventDefault(); openSection(s.id); }}
                        data-testid={`help-popular-${i}`}
                        className="w-full text-left px-1 py-1.5 flex items-center gap-2.5 rounded-md hover:bg-muted/30 transition-colors"
                      >
                        <Icon className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
                        <span className="text-xs text-foreground truncate">{s.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {isAdmin && (
            <button onClick={openNewArticle} data-testid="help-add-article"
              className="w-full text-left px-3 py-2.5 rounded-lg text-xs flex items-center gap-2.5 mb-1 text-primary hover:bg-primary/10 border border-dashed border-primary/30">
              <Plus className="w-4 h-4 flex-shrink-0" /> <span>New article</span>
            </button>
          )}
          {helpGroups.map(group => {
            const GIcon = group.icon;
            return (
              <div key={group.id} className="mb-3" data-testid={`help-group-${group.id}`}>
                <div className="px-2 pt-2 pb-1 flex items-center gap-2">
                  {GIcon ? <GIcon className="w-3.5 h-3.5 flex-shrink-0 text-primary/70" /> : null}
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-foreground/70">{group.label}</span>
                </div>
                <div className="ml-3 pl-2 border-l border-border/40 space-y-0.5">
                  {group.items.map(section => {
                    const Icon = section.icon;
                    return (
                      <button key={section.id} onClick={() => openSection(section.id)}
                        data-testid={`help-section-${section.id}`}
                        className={`w-full text-left px-2.5 py-2 rounded-lg text-xs flex items-center gap-2.5 transition-all ${activeSection === section.id ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}>
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{section.title}</span>
                        {activeSection === section.id && <ChevronRight className="w-3 h-3 ml-auto flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8 max-w-4xl" data-testid="help-content">
        {activeContent && (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary" className="text-[9px]">{activeContent.title}</Badge>
              {isAdmin && (
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={openEditArticle} data-testid="help-edit-article">
                    <Pencil className="w-3 h-3" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={deleteArticle} data-testid="help-delete-article">
                    <Trash2 className="w-3 h-3" /> Delete
                  </Button>
                </div>
              )}
            </div>
            {renderContent(activeContent.content)}
            {(() => {
              const voted = helpVotes[activeContent.id];
              return (
                <div className="mt-8 pt-5 border-t border-border/40 flex items-center gap-3 flex-wrap" data-testid="help-feedback">
                  {voted === undefined ? (
                    <>
                      <span className="text-xs text-muted-foreground">Was this helpful?</span>
                      <button
                        onClick={() => sendHelpVote(activeContent.id, true)}
                        data-testid="help-feedback-yes"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-border/50 text-foreground/80 hover:border-emerald-500/50 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                      >
                        <ThumbsUp className="w-3.5 h-3.5" /> Yes
                      </button>
                      <button
                        onClick={() => sendHelpVote(activeContent.id, false)}
                        data-testid="help-feedback-no"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-border/50 text-foreground/80 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <ThumbsDown className="w-3.5 h-3.5" /> No
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap" data-testid="help-feedback-thanks">
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                        {voted ? <ThumbsUp className="w-3.5 h-3.5 text-emerald-400" /> : <ThumbsDown className="w-3.5 h-3.5 text-red-400" />}
                        Thanks for your feedback!
                      </span>
                      <button
                        onClick={() => sendHelpVote(activeContent.id, !voted)}
                        data-testid="help-feedback-change"
                        className="text-[11px] text-muted-foreground/70 hover:text-foreground underline underline-offset-2"
                      >
                        Change to {voted ? 'No' : 'Yes'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Admin: edit / create article */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">{editDraft.isNew ? 'New Help Article' : 'Edit Help Article'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {editDraft.isNew && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Article id (slug)</Label>
                <Input data-testid="help-edit-id" value={editDraft.article_id} onChange={e => setEditDraft(d => ({ ...d, article_id: e.target.value }))} placeholder="my-new-article" className="rounded-lg bg-muted/50 border-border/50" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Title</Label>
              <Input data-testid="help-edit-title" value={editDraft.title} onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Content (Markdown)</Label>
              <Textarea data-testid="help-edit-content" value={editDraft.content} onChange={e => setEditDraft(d => ({ ...d, content: e.target.value }))} rows={16} className="rounded-lg bg-muted/50 border-border/50 font-mono text-xs" />
            </div>
            <Button data-testid="help-edit-save" onClick={saveArticle} disabled={editSaving} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95">
              <Save className="w-4 h-4 mr-2" />{editSaving ? 'Saving…' : (editDraft.isNew ? 'Create Article' : 'Save Changes')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Exposed for the in-app HelpPopover so it can render a short preview for the
// current page without duplicating the source-of-truth content.
export { SECTIONS as HELP_SECTIONS };

/**
 * Build a ~220-character preview for a section id — the first non-empty
 * paragraph after the `## Heading`. Skips headers, tables, lists, code
 * fences. Returns `null` if the section is unknown or has no prose.
 */
export function getHelpPreview(sectionId) {
  const section = SECTIONS.find(s => s.id === sectionId);
  if (!section) return null;
  const lines = section.content.trim().split('\n');
  const prose = [];
  let seenHeading = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (prose.length) break; // end of first paragraph
      continue;
    }
    if (line.startsWith('## ')) { seenHeading = true; continue; }
    if (!seenHeading) continue;
    if (line.startsWith('#')) continue;
    if (line.startsWith('|')) continue;
    if (line.startsWith('-') || line.startsWith('*') || /^\d+\./.test(line)) continue;
    if (line.startsWith('`')) continue;
    prose.push(line);
    if (prose.join(' ').length > 220) break;
  }
  const text = prose.join(' ').replace(/\*\*(.*?)\*\*/g, '$1').replace(/`([^`]+)`/g, '$1');
  if (!text) return { title: section.title, preview: null, icon: section.icon };
  const capped = text.length > 240 ? text.slice(0, 237).trimEnd() + '…' : text;
  return { title: section.title, preview: capped, icon: section.icon };
}
